// src/components/Button.tsx
import React from "react";
import { HiOutlineSparkles } from "react-icons/hi";
import { jsx, jsxs } from "react/jsx-runtime";
var Button = React.forwardRef(
  ({
    variant = "primary",
    size = "md",
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    disabled,
    className = "",
    children,
    ...restProps
  }, ref) => {
    const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-xtnd-dark disabled:opacity-50 disabled:cursor-not-allowed";
    const variantClasses = {
      primary: "bg-xtnd-blue text-xtnd-white hover:bg-xtnd-blue-700 focus:ring-xtnd-blue-500",
      secondary: "bg-xtnd-dark text-xtnd-white hover:bg-xtnd-dark-700 focus:ring-xtnd-dark-500",
      outline: "border border-xtnd-dark-300 bg-xtnd-white text-xtnd-dark hover:bg-xtnd-light focus:ring-xtnd-blue-500 dark:bg-xtnd-dark-800 dark:text-xtnd-white dark:border-xtnd-dark-600 dark:hover:bg-xtnd-dark-700",
      ghost: "text-xtnd-dark hover:bg-xtnd-light focus:ring-xtnd-dark-500 dark:text-xtnd-white dark:hover:bg-xtnd-dark-700",
      destructive: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
      social: "border-2 border-gray-200 bg-white text-xtnd-dark hover:border-xtnd-blue hover:bg-xtnd-light/20 focus:ring-xtnd-blue-500 dark:bg-xtnd-dark-800 dark:border-gray-600 dark:text-xtnd-white dark:hover:border-xtnd-blue dark:hover:bg-xtnd-dark-700 dark:hover:text-xtnd-white transition-all duration-200"
    };
    const sizeClasses3 = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base",
      xl: "px-8 py-4 text-lg"
    };
    const widthClass = fullWidth ? "w-full" : "";
    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses3[size]} ${widthClass} ${className}`.trim();
    return /* @__PURE__ */ jsxs("button", { ref, disabled: disabled || loading, className: classes, ...restProps, children: [
      loading && /* @__PURE__ */ jsx(HiOutlineSparkles, { className: "mr-2 h-4 w-4 animate-spin" }),
      !loading && leftIcon && /* @__PURE__ */ jsx("span", { className: "mr-2", children: leftIcon }),
      children,
      !loading && rightIcon && /* @__PURE__ */ jsx("span", { className: "ml-2", children: rightIcon })
    ] });
  }
);
Button.displayName = "Button";
var Button_default = Button;

// src/components/Input.tsx
import React2 from "react";
import { HiOutlineExclamationCircle, HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Input = React2.forwardRef(
  ({
    label,
    error,
    helperText,
    required = false,
    fullWidth = false,
    variant = "default",
    size = "md",
    leftIcon,
    rightIcon,
    showPasswordToggle = false,
    className = "",
    type = "text",
    disabled,
    id,
    onWheel,
    ...restProps
  }, ref) => {
    const [showPassword, setShowPassword] = React2.useState(false);
    const [inputType, setInputType] = React2.useState(type);
    React2.useEffect(() => {
      if (showPasswordToggle && type === "password") {
        setInputType(showPassword ? "text" : "password");
      } else {
        setInputType(type);
      }
    }, [showPassword, type, showPasswordToggle]);
    const baseClasses = "block transition-colors focus:outline-none focus:border-2 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed";
    const variantClasses = {
      default: "border border-xtnd-blue-300 bg-white focus:border-xtnd-blue focus:ring-xtnd-blue dark:border-xtnd-blue dark:bg-xtnd-dark-700 dark:text-xtnd-white",
      filled: "border-0 bg-gray-100 focus:bg-white focus:ring-blue-500 dark:bg-xtnd-dark-700 dark:text-xtnd-white",
      outlined: "border-2 border-gray-300 bg-transparent focus:border-blue-500 focus:ring-blue-500 dark:border-xtnd-dark-600 dark:bg-transparent dark:text-xtnd-white"
    };
    const sizeClasses3 = {
      sm: "px-3 py-1.5 text-sm rounded-md",
      md: "px-4 py-2 text-sm rounded-lg",
      lg: "px-5 py-3 text-base rounded-lg"
    };
    const errorClasses = error ? "!border-red-500 focus:border-red-500 focus:ring-red-500" : "";
    const widthClass = fullWidth ? "w-full" : "";
    const leftPadding = leftIcon ? "pl-10" : "";
    const rightPadding = rightIcon || showPasswordToggle ? "pr-10" : "";
    const inputClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses3[size]} ${errorClasses} ${widthClass} ${leftPadding} ${rightPadding} ${className}`.trim();
    const containerClasses = fullWidth ? "w-full" : "";
    const inputId = React2.useId();
    const resolvedId = id ?? inputId;
    const helperId = helperText ? `${resolvedId}-helper` : void 0;
    const errorId = error ? `${resolvedId}-error` : void 0;
    return /* @__PURE__ */ jsxs2("div", { className: containerClasses, children: [
      label && /* @__PURE__ */ jsxs2(
        "label",
        {
          htmlFor: resolvedId,
          className: "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1",
          children: [
            label,
            required && /* @__PURE__ */ jsx2("span", { className: "text-red-500 ml-1", children: "*" })
          ]
        }
      ),
      /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
        leftIcon && /* @__PURE__ */ jsx2(
          "div",
          {
            className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsx2("span", { className: "text-gray-400 dark:text-gray-300", children: leftIcon })
          }
        ),
        /* @__PURE__ */ jsx2(
          "input",
          {
            ref,
            type: inputType,
            disabled,
            className: inputClasses,
            id: resolvedId,
            "aria-invalid": !!error || void 0,
            "aria-describedby": errorId || helperId ? [errorId, helperId].filter(Boolean).join(" ") : void 0,
            onWheel: (e) => {
              if (type === "number") {
                e.currentTarget.blur();
              }
              onWheel?.(e);
            },
            ...restProps
          }
        ),
        showPasswordToggle && type === "password" && /* @__PURE__ */ jsx2(
          "button",
          {
            type: "button",
            className: "absolute inset-y-0 right-0 pr-3 flex items-center",
            onClick: () => setShowPassword(!showPassword),
            "aria-label": showPassword ? "Hide password" : "Show password",
            children: showPassword ? /* @__PURE__ */ jsx2(HiOutlineEyeOff, { className: "h-5 w-5 text-gray-400 hover:text-gray-200" }) : /* @__PURE__ */ jsx2(HiOutlineEye, { className: "h-5 w-5 text-gray-400 hover:text-gray-200" })
          }
        ),
        !showPasswordToggle && rightIcon && /* @__PURE__ */ jsx2(
          "div",
          {
            className: "absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsx2("span", { className: "text-gray-400 dark:text-gray-300", children: rightIcon })
          }
        )
      ] }),
      (error || helperText) && /* @__PURE__ */ jsxs2("div", { className: "mt-1 flex items-center", children: [
        error && /* @__PURE__ */ jsxs2(Fragment, { children: [
          /* @__PURE__ */ jsx2(
            HiOutlineExclamationCircle,
            {
              className: "h-4 w-4 text-red-500 mr-1",
              "aria-hidden": "true"
            }
          ),
          /* @__PURE__ */ jsx2("span", { id: errorId, role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: error })
        ] }),
        !error && helperText && /* @__PURE__ */ jsx2("span", { id: helperId, className: "text-sm text-gray-600 dark:text-gray-300", children: helperText })
      ] })
    ] });
  }
);
Input.displayName = "Input";
var Input_default = Input;

// src/components/Card.tsx
import React3 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Card = React3.forwardRef(
  ({ variant = "default", padding = "md", className = "", children, ...props }, ref) => {
    const baseClasses = "rounded-lg transition-shadow";
    const variantClasses = {
      default: "bg-xtnd-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700 shadow-xtnd-md",
      outlined: "bg-xtnd-white dark:bg-xtnd-dark-800 border border-xtnd-dark-300 dark:border-xtnd-dark-600 shadow-xtnd-md",
      elevated: "bg-xtnd-white dark:bg-xtnd-dark-800 shadow-xtnd-lg border border-xtnd-light dark:border-xtnd-dark-700",
      filled: "bg-xtnd-light dark:bg-xtnd-dark-700 border border-xtnd-light dark:border-xtnd-dark-600 shadow-xtnd-md"
    };
    const paddingClasses = {
      none: "",
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
      xl: "p-8"
    };
    const classes = `${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`.trim();
    return /* @__PURE__ */ jsx3("div", { ref, className: classes, ...props, children });
  }
);
var CardHeader = React3.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `border-b border-xtnd-light dark:border-xtnd-dark-700 pb-4 mb-4 ${className}`.trim();
    return /* @__PURE__ */ jsx3("div", { ref, className: classes, ...props, children });
  }
);
var CardBody = React3.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `flex-1 ${className}`.trim();
    return /* @__PURE__ */ jsx3("div", { ref, className: classes, ...props, children });
  }
);
var CardFooter = React3.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `border-t border-xtnd-light pt-4 mt-4 ${className}`.trim();
    return /* @__PURE__ */ jsx3("div", { ref, className: classes, ...props, children });
  }
);
Card.displayName = "Card";
CardHeader.displayName = "CardHeader";
CardBody.displayName = "CardBody";
CardFooter.displayName = "CardFooter";
var Card_default = Card;

// src/components/Badge.tsx
import React4 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Badge = React4.forwardRef(
  ({ children, variant = "default", size = "md", className = "", ...props }, ref) => {
    const baseClasses = "inline-flex items-center font-medium rounded-full";
    const sizeClasses3 = {
      sm: "px-2 py-1 text-xs",
      md: "px-2.5 py-1.5 text-sm",
      lg: "px-3 py-2 text-base"
    };
    const variantClasses = {
      default: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      primary: "bg-xtnd-blue text-xtnd-white",
      secondary: "bg-xtnd-dark text-xtnd-white",
      success: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      error: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
    };
    return /* @__PURE__ */ jsx4(
      "span",
      {
        ref,
        className: `
          ${baseClasses}
          ${sizeClasses3[size]}
          ${variantClasses[variant]}
          ${className}
        `.trim(),
        ...props,
        children
      }
    );
  }
);
Badge.displayName = "Badge";
var Badge_default = Badge;

// src/components/Dialog.tsx
import React5 from "react";
import { createPortal } from "react-dom";

// src/icons/Icon.tsx
import * as HeroIcons from "react-icons/hi";
import * as HeroIconsV2 from "react-icons/hi2";
import * as FeatherIcons from "react-icons/fi";
import * as FontAwesome from "react-icons/fa";

// src/icons/XTNDLogo.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function XTNDLogo({ className = "", size = "md" }) {
  const sizeClasses3 = {
    sm: "h-6 w-auto",
    md: "h-8 w-auto",
    lg: "h-18 w-auto",
    xl: "h-20 w-auto",
    "2xl": "h-24 w-auto"
  };
  return /* @__PURE__ */ jsx5("img", { src: "/xtnd.png", alt: "XTND Logo", className: `${sizeClasses3[size]} ${className}` });
}

// src/icons/XTNDLogoDark.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function XTNDLogoDark({ className = "", size = "md" }) {
  const sizeClasses3 = {
    sm: "h-6 w-auto",
    md: "h-8 w-auto",
    lg: "h-18 w-auto",
    xl: "h-20 w-auto",
    "2xl": "h-24 w-auto"
  };
  return /* @__PURE__ */ jsx6("img", { src: "/xtnd-dark.png", alt: "XTND Logo", className: `${sizeClasses3[size]} ${className}` });
}

// src/icons/XTNDLogoSquare.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function XTNDLogoSquare({ className = "", size = "md" }) {
  const sizeClasses3 = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-14 w-14",
    xl: "h-16 w-16"
  };
  return /* @__PURE__ */ jsx7(
    "img",
    {
      src: "/xtnd-logo-square.png",
      alt: "XTND Logo",
      className: `${sizeClasses3[size]} ${className}`
    }
  );
}

// src/icons/XTNDLogoSquareDark.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function XTNDLogoSquareDark({ className = "", size = "md" }) {
  const sizeClasses3 = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
    xl: "h-16 w-16"
  };
  return /* @__PURE__ */ jsx8(
    "img",
    {
      src: "/xtnd-logo-square-dark.png",
      alt: "XTND Logo",
      className: `${sizeClasses3[size]} ${className}`
    }
  );
}

// src/icons/GoogleIcon.tsx
import { jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
function GoogleIcon({ size = "md", className = "" }) {
  const sizeClasses3 = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };
  return /* @__PURE__ */ jsxs3("svg", { className: `${sizeClasses3[size]} ${className}`, viewBox: "0 0 24 24", children: [
    /* @__PURE__ */ jsx9(
      "path",
      {
        fill: "#4285F4",
        d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      }
    ),
    /* @__PURE__ */ jsx9(
      "path",
      {
        fill: "#34A853",
        d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      }
    ),
    /* @__PURE__ */ jsx9(
      "path",
      {
        fill: "#FBBC05",
        d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      }
    ),
    /* @__PURE__ */ jsx9(
      "path",
      {
        fill: "#EA4335",
        d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      }
    )
  ] });
}

// src/icons/MicrosoftIcon.tsx
import { jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
function MicrosoftIcon({ size = "md", className = "" }) {
  const sizeClasses3 = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };
  return /* @__PURE__ */ jsxs4("svg", { className: `${sizeClasses3[size]} ${className}`, viewBox: "0 0 24 24", children: [
    /* @__PURE__ */ jsx10("path", { fill: "#F25022", d: "M1 1h10v10H1z" }),
    /* @__PURE__ */ jsx10("path", { fill: "#00A4EF", d: "M13 1h10v10H13z" }),
    /* @__PURE__ */ jsx10("path", { fill: "#7FBA00", d: "M1 13h10v10H1z" }),
    /* @__PURE__ */ jsx10("path", { fill: "#FFB900", d: "M13 13h10v10H13z" })
  ] });
}

// src/icons/ClioIcon.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
function ClioIcon({ size = "md", className = "" }) {
  const sizeClasses3 = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };
  return /* @__PURE__ */ jsx11("img", { src: "/clio-logo.svg", alt: "Clio Logo", className: `${sizeClasses3[size]} ${className}` });
}

// src/icons/SecurityIcon.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function SecurityIcon({ size = "md", className = "" }) {
  const sizeClasses3 = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  return /* @__PURE__ */ jsx12(
    "svg",
    {
      className: `${sizeClasses3[size]} ${className}`,
      fill: "none",
      stroke: "currentColor",
      viewBox: "0 0 24 24",
      children: /* @__PURE__ */ jsx12(
        "path",
        {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: 2,
          d: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        }
      )
    }
  );
}

// src/icons/LoadingSpinner.tsx
import { jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
function LoadingSpinner({ size = "md", className = "" }) {
  const sizeClasses3 = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  return /* @__PURE__ */ jsxs5(
    "svg",
    {
      className: `animate-spin ${sizeClasses3[size]} ${className}`,
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      children: [
        /* @__PURE__ */ jsx13(
          "circle",
          {
            className: "opacity-25",
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx13(
          "path",
          {
            className: "opacity-75",
            fill: "currentColor",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          }
        )
      ]
    }
  );
}

// src/icons/Icon.tsx
import { jsx as jsx14, jsxs as jsxs6 } from "react/jsx-runtime";
var InfoIcon = (props) => /* @__PURE__ */ jsxs6(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    ...props,
    children: [
      /* @__PURE__ */ jsx14("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx14("path", { d: "M12 16v-4" }),
      /* @__PURE__ */ jsx14("path", { d: "M12 8h.01" })
    ]
  }
);
var ICON_REGISTRY = {
  // XTND Brand
  XTNDLogo,
  XTNDLogoDark,
  XTNDLogoSquare,
  XTNDLogoSquareDark,
  // Social Login
  GoogleIcon,
  MicrosoftIcon,
  ClioIcon,
  SecurityIcon,
  LoadingSpinner,
  // Custom Icons
  InfoIcon,
  // HeroIcons v1 (hi)
  ...HeroIcons,
  // HeroIcons v2 (hi2) - with aliases to avoid conflicts
  HiEyeSlash: HeroIconsV2.HiEyeSlash,
  HiAdjustmentsHorizontal: HeroIconsV2.HiAdjustmentsHorizontal,
  HiEllipsisVertical: HeroIconsV2.HiEllipsisVertical,
  HiMagnifyingGlass: HeroIconsV2.HiMagnifyingGlass,
  HiCalendarDays: HeroIconsV2.HiCalendarDays,
  HiArrowTrendingUpV2: HeroIconsV2.HiArrowTrendingUp,
  HiArrowTrendingDownV2: HeroIconsV2.HiArrowTrendingDown,
  HiCog6Tooth: HeroIconsV2.HiCog6Tooth,
  HiDocumentTextV2: HeroIconsV2.HiDocumentText,
  HiArrowPath: HeroIconsV2.HiArrowPath,
  HiCloudArrowUp: HeroIconsV2.HiCloudArrowUp,
  HiChartBarSquare: HeroIconsV2.HiChartBarSquare,
  HiComputerDesktop: HeroIconsV2.HiComputerDesktop,
  HiEnvelope: HeroIconsV2.HiEnvelope,
  HiBolt: HeroIconsV2.HiBolt,
  HiClipboardDocumentList: HeroIconsV2.HiClipboardDocumentList,
  HiWrenchScrewdriver: HeroIconsV2.HiWrenchScrewdriver,
  HiPhoto: HeroIconsV2.HiPhoto,
  HiArrowsUpDown: HeroIconsV2.HiArrowsUpDown,
  // Feather Icons (Fi)
  ...FeatherIcons,
  // Font Awesome Icons (Fa)
  ...FontAwesome
};
var SIZE_CLASSES = {
  xs: "w-3 h-3",
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
  "2xl": "w-10 h-10"
};
var Icon = ({
  name,
  size = "md",
  className = "",
  onClick,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden,
  ...props
}) => {
  const IconComponent = ICON_REGISTRY[name];
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in registry`);
    return null;
  }
  const sizeClass = SIZE_CLASSES[size];
  const combinedClassName = `${sizeClass} ${className}`.trim();
  const isCustomIcon = [
    "XTNDLogo",
    "XTNDFullLogo",
    "GoogleIcon",
    "MicrosoftIcon",
    "ClioIcon",
    "SecurityIcon",
    "LoadingSpinner",
    "InfoIcon"
  ].includes(name);
  if (isCustomIcon) {
    return /* @__PURE__ */ jsx14(
      IconComponent,
      {
        className: combinedClassName,
        onClick,
        "aria-label": ariaLabel,
        "aria-hidden": ariaHidden,
        ...props
      }
    );
  }
  return /* @__PURE__ */ jsx14(
    IconComponent,
    {
      className: combinedClassName,
      onClick,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHidden,
      ...props
    }
  );
};
Icon.displayName = "Icon";

// src/components/Dialog.tsx
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var DialogA11yContext = React5.createContext(null);
var Dialog = React5.forwardRef(
  ({
    open,
    onClose,
    size = "md",
    closeOnBackdrop = true,
    closeOnEscape = true,
    showCloseButton = true,
    ariaLabel,
    children,
    className = "",
    ...props
  }, ref) => {
    const dialogRef = React5.useRef(null);
    const overlayRef = React5.useRef(null);
    const previouslyFocusedRef = React5.useRef(null);
    const generatedHeaderId = React5.useId();
    const headerId = generatedHeaderId;
    React5.useImperativeHandle(ref, () => dialogRef.current);
    React5.useEffect(() => {
      if (!closeOnEscape) return;
      const handleEscape = (e) => {
        if (e.key === "Escape" && open) {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onClose, closeOnEscape]);
    React5.useEffect(() => {
      if (open) {
        previouslyFocusedRef.current = document.activeElement;
        document.body.style.overflow = "hidden";
        setTimeout(() => {
          if (!dialogRef.current) return;
          const focusable = dialogRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) {
            focusable[0]?.focus();
          } else {
            dialogRef.current.focus();
          }
        }, 0);
      } else {
        document.body.style.overflow = "unset";
        if (previouslyFocusedRef.current) {
          previouslyFocusedRef.current.focus();
        }
      }
      return () => {
        document.body.style.overflow = "unset";
        if (previouslyFocusedRef.current) {
          previouslyFocusedRef.current.focus();
        }
      };
    }, [open]);
    const handleBackdropClick = (e) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose();
    };
    const handleBackdropKeyDown = (e) => {
      if (!closeOnBackdrop) return;
      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        e.preventDefault();
        onClose();
      }
    };
    React5.useEffect(() => {
      if (!open || !dialogRef.current) return;
      const element = dialogRef.current;
      const handler = (e) => {
        if (e.key !== "Tab") return;
        const focusable = element.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !active || !element.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      };
      element.addEventListener("keydown", handler);
      return () => element.removeEventListener("keydown", handler);
    }, [open]);
    if (!open) return null;
    const sizeClasses3 = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      xxl: "max-w-[48rem]",
      xxllg: "max-w-[70rem]",
      full: "max-w-full mx-4"
    };
    const dialogContent = /* @__PURE__ */ jsx15("div", { ref: overlayRef, className: "fixed inset-0 z-50", children: /* @__PURE__ */ jsxs7(
      "div",
      {
        className: "flex h-full w-full items-center justify-center ",
        role: "button",
        "aria-label": "Close dialog by clicking backdrop",
        tabIndex: 0,
        onClick: handleBackdropClick,
        onKeyDown: handleBackdropKeyDown,
        children: [
          /* @__PURE__ */ jsx15("div", { className: "fixed inset-0 bg-black/60 transition-opacity" }),
          /* @__PURE__ */ jsx15(DialogA11yContext.Provider, { value: { headerId }, children: /* @__PURE__ */ jsxs7(
            "div",
            {
              ref: dialogRef,
              className: [
                "relative mx-auto my-4 max-h-[calc(100vh-2rem)]",
                "border border-gray-200 bg-white rounded-lg shadow-xl w-full px-4",
                "transform transition-all dark:bg-xtnd-dark-800 dark:text-xtnd-white dark:border-xtnd-dark-600",
                sizeClasses3[size],
                className
              ].join(" "),
              role: "dialog",
              "aria-modal": "true",
              "aria-label": ariaLabel,
              "aria-labelledby": ariaLabel ? void 0 : headerId,
              tabIndex: -1,
              ...props,
              children: [
                showCloseButton && /* @__PURE__ */ jsx15(
                  "button",
                  {
                    type: "button",
                    className: "absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700",
                    onClick: onClose,
                    "aria-label": "Close dialog",
                    children: /* @__PURE__ */ jsx15(Icon, { name: "HiOutlineX" })
                  }
                ),
                children
              ]
            }
          ) })
        ]
      }
    ) });
    return createPortal(dialogContent, document.body);
  }
);
var DialogHeader = React5.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600 ${className}`.trim();
    const a11y = React5.useContext(DialogA11yContext);
    return /* @__PURE__ */ jsx15("div", { ref, className: classes, id: a11y?.headerId, ...props, children });
  }
);
var DialogBody = React5.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 flex-1 ${className}`.trim();
    return /* @__PURE__ */ jsx15("div", { ref, className: classes, ...props, children });
  }
);
var DialogFooter = React5.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-t border-gray-200 dark:border-xtnd-dark-600 flex justify-end space-x-2 ${className}`.trim();
    return /* @__PURE__ */ jsx15("div", { ref, className: classes, ...props, children });
  }
);
Dialog.displayName = "Dialog";
DialogHeader.displayName = "DialogHeader";
DialogBody.displayName = "DialogBody";
DialogFooter.displayName = "DialogFooter";
var Dialog_default = Dialog;

// src/components/Label.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
function FormLabel({ htmlFor, children, className = "", ...props }) {
  return /* @__PURE__ */ jsx16(
    "label",
    {
      htmlFor,
      className: `block text-sm font-medium text-gray-700 dark:text-gray-200 ${className}`,
      ...props,
      children
    }
  );
}
var Label = FormLabel;
var Label_default = Label;

// src/components/Tooltip.tsx
import { useState, useRef, useEffect } from "react";
import { Fragment as Fragment2, jsx as jsx17, jsxs as jsxs8 } from "react/jsx-runtime";
var Tooltip = ({
  content,
  children,
  position = "top",
  className = "",
  disabled = false,
  maxWidth = "max-w-2xl",
  contentClassName = ""
}) => {
  const [calculatedPosition, setCalculatedPosition] = useState(
    position === "auto" ? "top" : position
  );
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  useEffect(() => {
    if (position === "auto" && isVisible && tooltipRef.current && triggerRef.current) {
      const trigger = triggerRef.current;
      const rect = trigger.getBoundingClientRect();
      const space = {
        top: rect.top,
        bottom: window.innerHeight - rect.bottom,
        left: rect.left,
        right: window.innerWidth - rect.right
      };
      let bestPosition = "top";
      let maxSpace = space.top;
      if (space.bottom > maxSpace) {
        maxSpace = space.bottom;
        bestPosition = "bottom";
      }
      if (space.left > maxSpace) {
        maxSpace = space.left;
        bestPosition = "left";
      }
      if (space.right > maxSpace) {
        maxSpace = space.right;
        bestPosition = "right";
      }
      setCalculatedPosition(bestPosition);
    }
  }, [position, isVisible]);
  if (disabled || !content) {
    return /* @__PURE__ */ jsx17(Fragment2, { children });
  }
  const effectivePosition = position === "auto" ? calculatedPosition : position;
  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2"
  };
  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 dark:border-b-gray-700 border-l-transparent border-r-transparent border-t-transparent",
    left: "left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700 border-t-transparent border-b-transparent border-r-transparent",
    right: "right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700 border-t-transparent border-b-transparent border-l-transparent"
  };
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      ref: triggerRef,
      className: `relative inline-block group ${className}`,
      onMouseEnter: () => setIsVisible(true),
      onMouseLeave: () => setIsVisible(false),
      children: [
        children,
        /* @__PURE__ */ jsx17(
          "div",
          {
            ref: tooltipRef,
            className: `absolute z-50 hidden group-hover:block ${positionClasses[effectivePosition]}`,
            children: /* @__PURE__ */ jsxs8("div", { className: "relative", children: [
              /* @__PURE__ */ jsx17(
                "div",
                {
                  className: `px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg whitespace-normal break-words min-w-[120px] w-fit ${maxWidth} ${contentClassName}`,
                  children: content
                }
              ),
              /* @__PURE__ */ jsx17("div", { className: `absolute w-0 h-0 border-4 ${arrowClasses[effectivePosition]}` })
            ] })
          }
        )
      ]
    }
  );
};
var Tooltip_default = Tooltip;

// src/components/Checkbox.tsx
import React7 from "react";
import { jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
var sizeClasses = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5"
};
var labelSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base"
};
var descriptionSizeClasses = {
  sm: "text-[10px]",
  md: "text-xs",
  lg: "text-sm"
};
var Checkbox = React7.forwardRef(
  ({
    label,
    description,
    size = "md",
    error = false,
    errorMessage,
    required = false,
    disabled = false,
    className = "",
    containerClassName = "",
    ...props
  }, ref) => {
    const checkboxClasses = `
      ${sizeClasses[size]}
      text-xtnd-blue
      border-gray-300 dark:border-gray-600
      rounded
      focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-0
      cursor-pointer
      transition-colors
      disabled:opacity-50 disabled:cursor-not-allowed
      ${error ? "border-red-500 dark:border-red-500 focus:ring-red-500" : ""}
      ${className}
    `;
    const checkbox = /* @__PURE__ */ jsx18("input", { ref, type: "checkbox", disabled, className: checkboxClasses, ...props });
    if (!label && !description) {
      return checkbox;
    }
    return /* @__PURE__ */ jsxs9("div", { className: containerClassName, children: [
      /* @__PURE__ */ jsxs9(
        "label",
        {
          className: `flex items-start space-x-3 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`,
          children: [
            /* @__PURE__ */ jsx18("div", { className: "flex items-center pt-0.5", children: checkbox }),
            /* @__PURE__ */ jsxs9("div", { className: "flex-1", children: [
              label && /* @__PURE__ */ jsxs9(
                "div",
                {
                  className: `font-medium text-gray-700 dark:text-gray-300 ${labelSizeClasses[size]}`,
                  children: [
                    label,
                    required && /* @__PURE__ */ jsx18("span", { className: "text-red-500 ml-1", "aria-label": "required", children: "*" })
                  ]
                }
              ),
              description && /* @__PURE__ */ jsx18(
                "p",
                {
                  className: `text-gray-500 dark:text-gray-400 mt-0.5 ${descriptionSizeClasses[size]}`,
                  children: description
                }
              )
            ] })
          ]
        }
      ),
      error && errorMessage && /* @__PURE__ */ jsx18("p", { className: "text-red-500 dark:text-red-400 text-xs mt-1 ml-6", children: errorMessage })
    ] });
  }
);
Checkbox.displayName = "Checkbox";
var Checkbox_default = Checkbox;

// src/components/Switch.tsx
import React8 from "react";
import { jsx as jsx19 } from "react/jsx-runtime";
var Switch = React8.forwardRef(
  ({
    checked,
    onChange,
    disabled = false,
    size = "md",
    className = "",
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...props
  }, ref) => {
    const sizeClasses3 = {
      sm: "h-4 w-7",
      md: "h-5 w-9",
      lg: "h-6 w-11"
    };
    const thumbSizeClasses = {
      sm: "h-3 w-3",
      md: "h-4 w-4",
      lg: "h-5 w-5"
    };
    const translateClasses = {
      sm: checked ? "translate-x-3" : "translate-x-0",
      md: checked ? "translate-x-4" : "translate-x-0",
      lg: checked ? "translate-x-5" : "translate-x-0"
    };
    return /* @__PURE__ */ jsx19(
      "button",
      {
        ref,
        type: "button",
        role: "switch",
        "aria-checked": checked,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        id,
        disabled,
        onClick: () => onChange(!checked),
        className: `
          relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2
          ${sizeClasses3[size]}
          ${checked ? "bg-xtnd-blue" : "bg-gray-200 dark:bg-gray-700"}
          ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
          ${className}
        `.trim(),
        ...props,
        children: /* @__PURE__ */ jsx19(
          "span",
          {
            className: `
            inline-block transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out
            ${thumbSizeClasses[size]}
            ${translateClasses[size]}
          `.trim()
          }
        )
      }
    );
  }
);
Switch.displayName = "Switch";
var Switch_default = Switch;

// src/components/LoadingSpinner.tsx
import React9 from "react";
import { jsx as jsx20 } from "react/jsx-runtime";
var LoadingSpinner2 = React9.forwardRef(
  ({
    size = "md",
    color = "primary",
    className = "",
    "aria-label": ariaLabel = "Loading...",
    ...props
  }, ref) => {
    const sizeClasses3 = {
      sm: "h-4 w-4",
      md: "h-6 w-6",
      lg: "h-8 w-8",
      xl: "h-12 w-12"
    };
    const colorClasses = {
      primary: "text-xtnd-blue",
      secondary: "text-xtnd-dark",
      white: "text-white",
      gray: "text-gray-600"
    };
    return /* @__PURE__ */ jsx20(
      "div",
      {
        ref,
        role: "status",
        "aria-label": ariaLabel,
        className: `
          inline-block animate-spin rounded-full border-2 border-solid border-current border-r-transparent
          ${sizeClasses3[size]}
          ${colorClasses[color]}
          ${className}
        `.trim(),
        ...props,
        children: /* @__PURE__ */ jsx20("span", { className: "sr-only", children: ariaLabel })
      }
    );
  }
);
LoadingSpinner2.displayName = "LoadingSpinner";
var LoadingSpinner_default = LoadingSpinner2;

// src/components/Textarea.tsx
import React10 from "react";
import { HiOutlineExclamationCircle as HiOutlineExclamationCircle2 } from "react-icons/hi";
import { Fragment as Fragment3, jsx as jsx21, jsxs as jsxs10 } from "react/jsx-runtime";
var Textarea = React10.forwardRef(
  ({
    label,
    error,
    helperText,
    required = false,
    fullWidth = false,
    variant = "default",
    resize = "vertical",
    autoResize = false,
    minRows = 3,
    maxRows = 10,
    className = "",
    disabled,
    ...restProps
  }, ref) => {
    const textareaRef = React10.useRef(null);
    React10.useImperativeHandle(ref, () => textareaRef.current);
    const adjustHeight = React10.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) return;
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;
      textarea.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
    }, [autoResize, minRows, maxRows]);
    React10.useEffect(() => {
      adjustHeight();
    }, [adjustHeight]);
    const baseClasses = "block w-full transition-colors focus:outline-none focus:border-2 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed";
    const variantClasses = {
      default: "border border-xtnd-blue-300 bg-white focus:border-xtnd-blue focus:ring-blue-500 dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white",
      filled: "border-0 bg-gray-100 focus:bg-white focus:ring-blue-500 dark:bg-xtnd-dark-700 dark:text-xtnd-white",
      outlined: "border-2 border-gray-300 bg-transparent focus:border-blue-500 focus:ring-blue-500 dark:border-xtnd-dark-600 dark:bg-transparent dark:text-xtnd-white"
    };
    const resizeClasses = {
      none: "resize-none",
      vertical: "resize-y",
      horizontal: "resize-x",
      both: "resize"
    };
    const errorClasses = error ? "!border-red-500 focus:border-red-500 focus:ring-red-500" : "";
    const widthClass = fullWidth ? "w-full" : "";
    const resizeClass = autoResize ? "resize-none" : resizeClasses[resize];
    const textareaClasses = `${baseClasses} ${variantClasses[variant]} ${errorClasses} ${widthClass} ${resizeClass} px-4 py-2 text-sm rounded-lg ${className}`.trim();
    const containerClasses = fullWidth ? "w-full" : "";
    return /* @__PURE__ */ jsxs10("div", { className: containerClasses, children: [
      label && /* @__PURE__ */ jsxs10("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1", children: [
        label,
        required && /* @__PURE__ */ jsx21("span", { className: "text-red-500 ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsx21(
        "textarea",
        {
          ref: textareaRef,
          disabled,
          rows: autoResize ? minRows : restProps.rows || minRows,
          className: textareaClasses,
          onInput: autoResize ? adjustHeight : void 0,
          ...restProps
        }
      ),
      (error || helperText) && /* @__PURE__ */ jsxs10("div", { className: "mt-1 flex items-center", children: [
        error && /* @__PURE__ */ jsxs10(Fragment3, { children: [
          /* @__PURE__ */ jsx21(HiOutlineExclamationCircle2, { className: "h-4 w-4 text-red-500 mr-1" }),
          /* @__PURE__ */ jsx21("span", { className: "text-sm text-red-600 dark:text-red-400", children: error })
        ] }),
        !error && helperText && /* @__PURE__ */ jsx21("span", { className: "text-sm text-gray-500 dark:text-gray-300", children: helperText })
      ] })
    ] });
  }
);
Textarea.displayName = "Textarea";
var Textarea_default = Textarea;

// src/components/Select.tsx
import React11 from "react";
import { HiOutlineExclamationCircle as HiOutlineExclamationCircle3 } from "react-icons/hi";
import ReactSelect from "react-select";
import { jsx as jsx22, jsxs as jsxs11 } from "react/jsx-runtime";
var ReactSelectComponent = ReactSelect;
var getTheme = (hasError) => ({
  control: (state) => `
    bg-xtnd-white text-xtnd-dark-900 rounded-xtnd border
    dark:bg-xtnd-dark-700 dark:text-xtnd-white
    ${hasError ? "border-red-500 dark:border-red-500 focus:border-red-500 focus:ring-red-500" : "border-xtnd-light dark:border-xtnd-dark-600 hover:border-xtnd-blue dark:hover:border-xtnd-blue"}
    ${state.isFocused && !hasError ? "border-xtnd-blue" : ""}
    ${state.isDisabled ? `bg-xtnd-light-100 cursor-not-allowed opacity-50
                          dark:bg-xtnd-dark-800` : ""}
  `,
  valueContainer: () => "flex flex-wrap gap-1 px-2 py-1 flex-1 min-w-0",
  menu: () => `
    mt-1 rounded-xtnd border border-xtnd-light bg-xtnd-blue-50 shadow-xtnd-md
    dark:border-xtnd-dark-600 dark:text-xtnd-white dark:bg-xtnd-dark-700
    [&[data-placement="top"]]:mb-1 [&[data-placement="top"]]:mt-0
  `,
  menuList: () => "py-1 max-h-[200px] overflow-y-auto",
  option: (state) => {
    const option = state.data;
    const hasColor = !!option?.color;
    if (hasColor) {
      return "px-3 py-2 cursor-pointer text-sm transition-colors";
    }
    return `px-3 py-2 cursor-pointer text-sm transition-colors
      ${state.isSelected ? `bg-xtnd-blue text-xtnd-dark-900 font-medium` : state.isFocused ? `bg-xtnd-light text-xtnd-dark-900
               dark:bg-xtnd-dark-600 dark:text-xtnd-white` : `text-xtnd-dark-900
               dark:text-xtnd-white`}
    `;
  },
  placeholder: () => `text-sm text-xtnd-dark-400
                      dark:text-xtnd-dark-300`,
  singleValue: (state) => {
    const option = state.data;
    if (option?.color) {
      return `break-words overflow-hidden text-[${option.color}]`;
    }
    return "break-words overflow-hidden";
  },
  input: () => `text-xtnd-dark-900 flex-1 min-w-0
                dark:text-xtnd-white`,
  multiValue: (state) => {
    const option = state.data;
    const hasColor = !!option?.color;
    if (hasColor) {
      return "text-sm flex rounded-sm font-medium max-w-full overflow-hidden";
    }
    return `text-sm flex font-medium max-w-full bg-xtnd-blue rounded-sm overflow-hidden
            dark:bg-xtnd-blue-900`;
  },
  multiValueLabel: () => {
    return "px-2 py-0.5 break-words overflow-hidden";
  },
  multiValueRemove: () => `px-1 flex items-center text-xtnd-dark-50 hover:bg-xtnd-blue-900`,
  // === Right-Side Icons (Dropdown Arrow, Clear Button) ===
  indicatorsContainer: (state) => `
    flex gap-1
    ${state.isDisabled ? `text-xtnd-dark-300
           dark:text-xtnd-dark-500` : `text-xtnd-dark-600
           dark:text-xtnd-dark-300`}
  `,
  clearIndicator: () => `p-2 cursor-pointer text-xtnd-dark-600 hover:text-xtnd-blue
     dark:text-xtnd-dark-300 dark:hover:text-xtnd-blue`,
  dropdownIndicator: () => `p-2 cursor-pointer text-xtnd-dark-600 hover:text-xtnd-blue
     dark:text-xtnd-dark-300 dark:hover:text-xtnd-blue`,
  loadingIndicator: () => "text-xtnd-blue",
  indicatorSeparator: () => `self-stretch w-px bg-xtnd-light
                             dark:bg-xtnd-dark-400 my-2`
});
var theme = getTheme(false);
var getColorOptionStyles = (isDarkMode) => ({
  menu: (base, state) => {
    const styles = {
      ...base,
      zIndex: 9999
    };
    if (state.placement === "top") {
      styles.marginBottom = "4px";
      styles.marginTop = "0";
    }
    return styles;
  },
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  option: (_base, { data, isFocused, isSelected }) => {
    const hasColor = !!data.color;
    if (!hasColor) return {};
    return {
      backgroundColor: isSelected ? isDarkMode ? data?.bgColor || data?.color : data?.color : isFocused ? `${data.color}20` : "transparent",
      color: isSelected ? "#ffffff" : data.color
      // White text when selected
    };
  },
  singleValue: (base, { data }) => {
    if (data?.color) {
      return {
        ...base,
        color: data.color
      };
    }
    return base;
  },
  multiValue: (_base, { data }) => {
    const hasColor = !!data.color;
    if (!hasColor) return {};
    return {
      color: "#ffffff",
      backgroundColor: `${isDarkMode ? data?.bgColor || data?.color : data?.color}`
    };
  },
  multiValueRemove: () => {
    return {
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.2)"
      }
    };
  }
});
function SelectComponent(props, ref) {
  const {
    id,
    options,
    value,
    onChange,
    placeholder = "Select an option",
    isLoading = false,
    noOptionsMessage = () => "No options found",
    showOptionBullets = false,
    closeMenuOnSelect = true,
    hideSelectedOptions = false,
    menuPlacement = "auto",
    menuPosition = "fixed",
    menuPortalTarget = typeof document !== "undefined" ? document.body : null,
    menuShouldScrollIntoView = true,
    className = "",
    classNamePrefix = "react-select",
    label,
    error,
    helperText,
    required = false,
    disabled = false,
    multiple = false,
    searchable = false,
    clearable = false,
    fullWidth = false,
    size = "md",
    onInputChange,
    isDarkMode = false
  } = props;
  const labelId = React11.useId();
  const helperId = helperText ? `${labelId}-helper` : void 0;
  const errorId = error ? `${labelId}-error` : void 0;
  const selectClasses = [
    "xtnd-select",
    `xtnd-select--${size}`,
    fullWidth ? "xtnd-select--full-width" : "",
    error ? "xtnd-select--error" : "",
    className
  ].filter(Boolean).join(" ");
  const getSelectValue = () => {
    if (typeof value === "string") {
      const findOption = (opts) => {
        for (const option of opts) {
          if ("options" in option) {
            const found = findOption(option.options);
            if (found) return found;
          } else if (option.value === value) {
            return option;
          }
        }
        return null;
      };
      return findOption(options);
    }
    return value;
  };
  const handleChange = (newValue) => {
    if (multiple) {
      onChange(
        newValue
      );
    } else {
      const singleValue = newValue;
      if (singleValue && typeof singleValue === "object" && "value" in singleValue) {
        if (typeof value === "string") {
          onChange(singleValue.value);
        } else {
          onChange(singleValue);
        }
      } else {
        onChange(singleValue);
      }
    }
  };
  return /* @__PURE__ */ jsxs11("div", { className: fullWidth ? "w-full" : "", ref, children: [
    label && /* @__PURE__ */ jsxs11(
      "label",
      {
        id: labelId,
        className: "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1",
        children: [
          label,
          required && /* @__PURE__ */ jsx22("span", { className: "text-red-500 ml-1", children: "*" })
        ]
      }
    ),
    /* @__PURE__ */ jsx22("div", { className: selectClasses, id, children: /* @__PURE__ */ jsx22(
      ReactSelectComponent,
      {
        options,
        value: getSelectValue(),
        onChange: handleChange,
        onInputChange,
        placeholder,
        unstyled: true,
        formatOptionLabel: (option, meta) => {
          if (meta.context === "menu") {
            if (showOptionBullets) {
              return /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx22(
                  "span",
                  {
                    className: "inline-block w-2 h-2 rounded-full shrink-0",
                    style: { backgroundColor: "currentColor" }
                  }
                ),
                /* @__PURE__ */ jsx22("span", { children: option?.label })
              ] });
            }
            return /* @__PURE__ */ jsx22("span", { children: option?.label });
          }
          return /* @__PURE__ */ jsx22("span", { children: option?.label });
        },
        isMulti: multiple,
        isClearable: clearable,
        isSearchable: searchable,
        isLoading,
        isDarkMode,
        isDisabled: disabled,
        noOptionsMessage,
        closeMenuOnSelect,
        hideSelectedOptions,
        menuPlacement,
        menuPosition,
        menuPortalTarget,
        menuShouldScrollIntoView,
        classNames: getTheme(!!error),
        styles: getColorOptionStyles(isDarkMode),
        classNamePrefix
      }
    ) }),
    (error || helperText) && /* @__PURE__ */ jsx22("div", { className: "mt-1", children: error ? /* @__PURE__ */ jsxs11("div", { className: "flex items-center", children: [
      /* @__PURE__ */ jsx22(
        HiOutlineExclamationCircle3,
        {
          className: "h-4 w-4 text-red-500 mr-1",
          "aria-hidden": "true"
        }
      ),
      /* @__PURE__ */ jsx22("span", { id: errorId, role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: error })
    ] }) : /* @__PURE__ */ jsx22("span", { id: helperId, className: "text-sm text-gray-600 dark:text-gray-300", children: helperText }) })
  ] });
}
var SelectForwardRef = React11.forwardRef(SelectComponent);
var Select = Object.assign(SelectForwardRef, {
  displayName: "Select"
});
var Select_default = Select;

// src/components/Pagination.tsx
import React12 from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { jsx as jsx23, jsxs as jsxs12 } from "react/jsx-runtime";
var Pagination = React12.forwardRef(
  ({
    currentPage,
    totalPages,
    totalRecords,
    hasNextPage,
    hasPreviousPage,
    isLoading = false,
    pageSize,
    pageSizeOptions = [25, 50, 100],
    onPageChange,
    onPageSizeChange,
    onNextPage,
    onPreviousPage,
    showPageSizeSelector = true,
    showRecordCount = true,
    className = "",
    cursorBased = false,
    currentItemsCount
  }, ref) => {
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const handlePageChange = (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        onPageChange(page);
      }
    };
    const handlePreviousPage = () => {
      if (hasPreviousPage) {
        if (onPreviousPage) {
          onPreviousPage();
        } else {
          handlePageChange(currentPage - 1);
        }
      }
    };
    const handleNextPage = () => {
      if (hasNextPage) {
        if (onNextPage) {
          onNextPage();
        } else {
          handlePageChange(currentPage + 1);
        }
      }
    };
    const getVisiblePageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push(-1);
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push(-1);
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push(-1);
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push(-2);
          pages.push(totalPages);
        }
      }
      return pages;
    };
    const visiblePages = getVisiblePageNumbers();
    const pageSizeOptionsSelect = pageSizeOptions.map((size) => ({
      value: size.toString(),
      label: size.toString()
    }));
    return /* @__PURE__ */ jsxs12(
      "div",
      {
        ref,
        className: `flex items-center justify-between px-6 py-4 bg-xtnd-white dark:bg-xtnd-dark-800 border-t border-xtnd-light dark:border-xtnd-dark-700 ${className}`,
        children: [
          showRecordCount && /* @__PURE__ */ jsx23("div", { className: "flex items-center text-sm text-xtnd-dark-600 dark:text-xtnd-light-100", children: cursorBased ? /* @__PURE__ */ jsxs12("span", { children: [
            "Page ",
            currentPage,
            " \u2022 ",
            currentItemsCount || 0,
            " of ",
            pageSize,
            " items per page"
          ] }) : /* @__PURE__ */ jsxs12("span", { children: [
            "Showing ",
            startRecord.toLocaleString(),
            " to ",
            endRecord.toLocaleString(),
            " of",
            " ",
            totalRecords.toLocaleString(),
            " results"
          ] }) }),
          /* @__PURE__ */ jsxs12("div", { className: "flex items-center space-x-1", children: [
            /* @__PURE__ */ jsx23(
              Button_default,
              {
                variant: "outline",
                size: "sm",
                onClick: handlePreviousPage,
                disabled: !hasPreviousPage || isLoading,
                leftIcon: /* @__PURE__ */ jsx23(HiChevronLeft, { className: "h-4 w-4" }),
                className: "min-w-[100px]",
                children: "Previous"
              }
            ),
            !cursorBased && /* @__PURE__ */ jsx23("div", { className: "flex items-center space-x-1", children: visiblePages.map((page, index) => {
              if (page === -1 || page === -2) {
                return /* @__PURE__ */ jsx23(
                  "div",
                  {
                    className: "px-2 py-2 text-xtnd-dark-400 dark:text-xtnd-dark-500",
                    children: "..."
                  },
                  `ellipsis-${index}`
                );
              }
              const isActive = page === currentPage;
              return /* @__PURE__ */ jsx23(
                Button_default,
                {
                  variant: isActive ? "primary" : "outline",
                  size: "sm",
                  onClick: () => handlePageChange(page),
                  disabled: isLoading,
                  className: `min-w-[40px] ${isActive ? "bg-xtnd-blue text-xtnd-white hover:bg-xtnd-blue-700" : "text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-xtnd-dark dark:hover:text-xtnd-white"}`,
                  children: page
                },
                page
              );
            }) }),
            /* @__PURE__ */ jsx23(
              Button_default,
              {
                variant: "outline",
                size: "sm",
                onClick: handleNextPage,
                disabled: !hasNextPage || isLoading,
                rightIcon: /* @__PURE__ */ jsx23(HiChevronRight, { className: "h-4 w-4" }),
                className: "min-w-[100px]",
                children: "Next"
              }
            )
          ] }),
          showPageSizeSelector && /* @__PURE__ */ jsxs12("div", { className: "flex items-center space-x-2", children: [
            /* @__PURE__ */ jsx23("span", { className: "text-sm text-xtnd-dark-600 dark:text-xtnd-light-100", children: "Show:" }),
            /* @__PURE__ */ jsx23(
              Select_default,
              {
                options: pageSizeOptionsSelect,
                value: pageSizeOptionsSelect.find((opt) => opt.value === pageSize.toString()) || null,
                onChange: (value) => {
                  if (value) {
                    const newSize = parseInt(value.value, 10);
                    onPageSizeChange(newSize);
                  }
                },
                size: "sm",
                multiple: false
              }
            )
          ] })
        ]
      }
    );
  }
);
Pagination.displayName = "Pagination";
var Pagination_default = Pagination;

// src/components/PressHoldButton.tsx
import { useState as useState2, useRef as useRef2, useCallback } from "react";
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var PressHoldButton = ({
  children,
  onPressHold,
  onRegularClick,
  holdDuration = 800,
  className = "",
  disabled = false,
  title
}) => {
  const [isHolding, setIsHolding] = useState2(false);
  const [progress, setProgress] = useState2(0);
  const timeoutRef = useRef2(null);
  const intervalRef = useRef2(null);
  const startTimeRef = useRef2(0);
  const buttonRef = useRef2(null);
  const originalEventRef = useRef2(null);
  const cleanupTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  const startHold = useCallback(
    (event) => {
      if (disabled) return;
      event.preventDefault();
      setIsHolding(true);
      setProgress(0);
      startTimeRef.current = Date.now();
      originalEventRef.current = event;
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const newProgress = Math.min(elapsed / holdDuration * 100, 100);
        setProgress(newProgress);
      }, 16);
      timeoutRef.current = setTimeout(() => {
        cleanupTimers();
        setIsHolding(false);
        setProgress(0);
        const syntheticEvent = {
          ...originalEventRef.current,
          currentTarget: buttonRef.current,
          target: buttonRef.current
        };
        onPressHold(syntheticEvent);
        originalEventRef.current = null;
      }, holdDuration);
    },
    [disabled, holdDuration, onPressHold, cleanupTimers]
  );
  const endHold = useCallback(
    (event) => {
      if (disabled) return;
      const elapsed = Date.now() - startTimeRef.current;
      const wasShortPress = elapsed < holdDuration;
      cleanupTimers();
      setIsHolding(false);
      setProgress(0);
      if (wasShortPress && onRegularClick) {
        onRegularClick(event);
      }
    },
    [disabled, holdDuration, onRegularClick, cleanupTimers]
  );
  const handleMouseDown = useCallback(
    (event) => {
      startHold(event);
    },
    [startHold]
  );
  const handleMouseUp = useCallback(
    (event) => {
      endHold(event);
    },
    [endHold]
  );
  const handleMouseLeave = useCallback(
    (event) => {
      endHold(event);
    },
    [endHold]
  );
  const handleTouchStart = useCallback(
    (event) => {
      startHold(event);
    },
    [startHold]
  );
  const handleTouchEnd = useCallback(
    (event) => {
      endHold(event);
    },
    [endHold]
  );
  const handleTouchCancel = useCallback(
    (event) => {
      endHold(event);
    },
    [endHold]
  );
  return /* @__PURE__ */ jsxs13(
    "button",
    {
      ref: buttonRef,
      className: `
        relative overflow-hidden
        px-3 py-2 rounded-md
        bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700
        text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100
        transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
        ${className}
      `,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
      disabled,
      title,
      type: "button",
      children: [
        isHolding && /* @__PURE__ */ jsx24("div", { className: "absolute inset-0 pointer-events-none", children: /* @__PURE__ */ jsx24(
          "div",
          {
            className: "h-full bg-blue-200 dark:bg-blue-800 opacity-30 transition-all duration-75 ease-out",
            style: { width: `${progress}%` }
          }
        ) }),
        isHolding && /* @__PURE__ */ jsx24("div", { className: "absolute inset-0 flex items-center justify-center pointer-events-none", children: /* @__PURE__ */ jsxs13("svg", { className: "w-8 h-8 -rotate-90", viewBox: "0 0 32 32", children: [
          /* @__PURE__ */ jsx24(
            "circle",
            {
              cx: "16",
              cy: "16",
              r: "12",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeOpacity: "0.3"
            }
          ),
          /* @__PURE__ */ jsx24(
            "circle",
            {
              cx: "16",
              cy: "16",
              r: "12",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeDasharray: `${2 * Math.PI * 12}`,
              strokeDashoffset: `${2 * Math.PI * 12 * (1 - progress / 100)}`,
              className: "text-blue-500 transition-all duration-75 ease-out"
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx24(
          "div",
          {
            className: `relative z-10 flex items-center justify-center ${isHolding ? "opacity-50" : ""}`,
            children
          }
        )
      ]
    }
  );
};
PressHoldButton.displayName = "PressHoldButton";
var PressHoldButton_default = PressHoldButton;

// src/components/LastUpdated.tsx
import React14 from "react";
import { HiClock } from "react-icons/hi";
import { jsx as jsx25, jsxs as jsxs14 } from "react/jsx-runtime";
var LastUpdated = ({
  timestamp,
  label = "Updated",
  className = "",
  ...props
}) => {
  const formatted = React14.useMemo(() => {
    if (!timestamp) return "Never";
    try {
      const d = typeof timestamp === "string" || typeof timestamp === "number" ? new Date(timestamp) : timestamp;
      if (Number.isNaN(d.getTime())) return "Never";
      return d.toLocaleString();
    } catch {
      return "Never";
    }
  }, [timestamp]);
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: `flex items-center text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ${className}`.trim(),
      "aria-label": `${label}: ${formatted}`,
      ...props,
      children: [
        /* @__PURE__ */ jsx25(HiClock, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
        /* @__PURE__ */ jsxs14("span", { children: [
          label,
          ": ",
          formatted
        ] })
      ]
    }
  );
};
var LastUpdated_default = LastUpdated;

// src/components/DatePicker.tsx
import { useState as useState3, useEffect as useEffect2, useMemo, useRef as useRef3, useCallback as useCallback2 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { DayPicker } from "react-day-picker";
import defaultClassNames from "react-day-picker/dist/style.module.css";

// src/utils/date.ts
import {
  format as dateFnsFormat,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  subWeeks
} from "date-fns";
var formatDate = (date, pattern = "yyyy-MM-dd") => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.warn("formatDate: Invalid date provided");
    return "Invalid Date";
  }
  try {
    return dateFnsFormat(date, pattern);
  } catch (error) {
    console.error("formatDate: Error formatting date", error);
    return "Invalid Date";
  }
};
var getWeekStart = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("getWeekStart: Invalid date provided");
  }
  return startOfWeek(date, { weekStartsOn: 1 });
};
var getWeekEnd = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("getWeekEnd: Invalid date provided");
  }
  return endOfWeek(date, { weekStartsOn: 1 });
};
var getMonthStart = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("getMonthStart: Invalid date provided");
  }
  return startOfMonth(date);
};
var getMonthEnd = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("getMonthEnd: Invalid date provided");
  }
  return endOfMonth(date);
};
var subtractDays = (date, amount) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("subtractDays: Invalid date provided");
  }
  return subDays(date, amount);
};
var subtractWeeks = (date, amount) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("subtractWeeks: Invalid date provided");
  }
  return subWeeks(date, amount);
};

// src/components/DatePicker.tsx
import { Fragment as Fragment4, jsx as jsx26, jsxs as jsxs15 } from "react/jsx-runtime";
var DATE_RANGE_PRESETS = [
  {
    label: "Last Week",
    id: "lastWeek",
    getRange: () => {
      const today = /* @__PURE__ */ new Date();
      const lastWeekStartDate = subtractWeeks(today, 1);
      return {
        from: getWeekStart(lastWeekStartDate),
        to: getWeekEnd(lastWeekStartDate)
      };
    }
  },
  {
    label: "Last 2 Weeks",
    id: "last2Weeks",
    getRange: () => {
      const today = /* @__PURE__ */ new Date();
      const twoWeeksAgo = subtractWeeks(today, 2);
      const oneWeekAgo = subtractWeeks(today, 1);
      return {
        from: getWeekStart(twoWeeksAgo),
        to: getWeekEnd(oneWeekAgo)
      };
    }
  },
  {
    label: "This Week",
    id: "thisWeek",
    getRange: () => {
      const today = /* @__PURE__ */ new Date();
      return { from: getWeekStart(today), to: getWeekEnd(today) };
    }
  },
  {
    label: "Last 30 Days",
    id: "last30Days",
    getRange: () => {
      const today = /* @__PURE__ */ new Date();
      return { from: subtractDays(today, 30), to: today };
    }
  },
  {
    label: "This Month",
    id: "thisMonth",
    getRange: () => {
      const today = /* @__PURE__ */ new Date();
      return { from: getMonthStart(today), to: getMonthEnd(today) };
    }
  }
];
var dayPickerStyles = {
  ...defaultClassNames,
  // Root container - clean white/dark background with subtle border
  root: `${defaultClassNames.root} border rounded-lg shadow-lg text-sm
    bg-white dark:bg-xtnd-dark-800
    border-gray-200 dark:border-xtnd-dark-600`,
  // Caption (month/year selector)
  caption_label: "hidden",
  chevron: "fill-xtnd-dark-600 dark:fill-xtnd-light hover:fill-xtnd-blue dark:hover:fill-xtnd-blue",
  // Dropdown selectors for month/year
  dropdown: `rounded-md text-sm font-medium px-2 py-1
    bg-white dark:bg-xtnd-dark-700
    text-xtnd-dark dark:text-xtnd-light
    border border-gray-300 dark:border-xtnd-dark-600
    hover:border-xtnd-blue dark:hover:border-xtnd-blue
    focus:outline-none focus:ring-2 focus:ring-xtnd-blue`,
  // Calendar grid
  months: `${defaultClassNames.months} rounded-lg w-full
    text-xtnd-dark dark:text-xtnd-light p-2`,
  // Weekday headers (S M T W T F S)
  weekday: `${defaultClassNames.weekday}
    text-xtnd-dark-600 dark:text-xtnd-dark-300
    font-semibold uppercase text-xs`,
  // Day cells
  day: `rounded-md transition-colors
    text-xtnd-dark dark:text-xtnd-light`,
  // Today's date - subtle underline with XTND blue
  today: `underline decoration-2 decoration-xtnd-blue
    font-semibold`,
  // Selected date(s) - XTND blue background with underline if today
  selected: `rounded-md font-bold
    bg-xtnd-blue text-white
    data-[today=true]:underline data-[today=true]:decoration-2 data-[today=true]:decoration-xtnd-blue`,
  // Outside month dates - muted
  outside: `text-gray-400 dark:text-xtnd-dark-500`,
  // Disabled dates
  disabled: "text-gray-300 dark:text-xtnd-dark-600 opacity-30 cursor-not-allowed",
  // Range selection styles
  range_start: "rounded-md",
  range_end: "rounded-md",
  range_middle: `bg-xtnd-blue-100 dark:bg-xtnd-dark-700
    text-xtnd-dark dark:text-xtnd-light
    rounded-none`
};
var formatWeekdayName = (day) => {
  return day.toLocaleDateString(void 0, { weekday: "narrow" });
};
var rangeValueToDateRange = (value) => {
  if (!value?.startDate) return void 0;
  return {
    from: value.startDate,
    to: value.endDate ?? void 0
  };
};
var dateRangeToRangeValue = (range) => {
  return {
    startDate: range?.from ?? null,
    endDate: range?.to ?? null
  };
};
var DateRangePresets = ({ onPresetSelect }) => /* @__PURE__ */ jsx26("div", { className: "p-4 bg-gray-50 dark:bg-xtnd-dark-900 rounded-b-lg w-0 min-w-full border-t border-gray-200 dark:border-xtnd-dark-600", children: /* @__PURE__ */ jsx26("div", { className: "flex flex-wrap gap-2", children: DATE_RANGE_PRESETS.map((preset) => /* @__PURE__ */ jsx26(
  Button,
  {
    variant: "outline",
    size: "sm",
    onClick: () => onPresetSelect(preset.getRange()),
    className: "text-xs hover:border-xtnd-blue transition-colors",
    children: preset.label
  },
  preset.id
)) }) });
var CALENDAR_GAP = 8;
function DatePicker(props) {
  const {
    mode,
    label,
    id,
    placeholder,
    className,
    buttonClassName,
    disabled,
    required,
    startYear = 1950,
    endYear = 2100,
    error,
    format = "MM/dd/yyyy",
    allowClear = false,
    min,
    max
  } = props;
  const [selected, setSelected] = useState3();
  const [isOpen, setIsOpen] = useState3(false);
  const prevIsOpenRef = useRef3(false);
  const buttonRef = useRef3(null);
  const calendarRef = useRef3(null);
  const [calendarStyles, setCalendarStyles] = useState3({ top: 0, left: 0 });
  const numberOfMonths = mode === "range" ? props.numberOfMonths ?? 2 : 1;
  useEffect2(() => {
    if (mode === "single") {
      setSelected(props.value ?? void 0);
    } else if (mode === "range") {
      setSelected(rangeValueToDateRange(props.value));
    }
  }, [props.value, mode]);
  useEffect2(() => {
    const prevIsOpen = prevIsOpenRef.current;
    if (prevIsOpen !== isOpen) {
      props.onOpenChange?.(isOpen);
      if (prevIsOpen && !isOpen) {
        props.onClose?.();
      }
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen, props]);
  const updatePosition = useCallback2(() => {
    if (!buttonRef.current || !calendarRef.current) return;
    const triggerRect = buttonRef.current.getBoundingClientRect();
    const calendarRect = calendarRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const shouldOpenAbove = viewportHeight - triggerRect.bottom < calendarRect.height + CALENDAR_GAP;
    const top = shouldOpenAbove ? triggerRect.top - calendarRect.height - CALENDAR_GAP : triggerRect.bottom + CALENDAR_GAP;
    let left = triggerRect.left;
    if (left + calendarRect.width > viewportWidth - CALENDAR_GAP) {
      left = viewportWidth - calendarRect.width - CALENDAR_GAP;
    }
    left = Math.max(CALENDAR_GAP, left);
    setCalendarStyles({
      top: Math.max(top, CALENDAR_GAP),
      left
    });
  }, []);
  useEffect2(() => {
    if (!isOpen) return;
    const rAF = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      cancelAnimationFrame(rAF);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);
  const handleSingleSelect = (newDate) => {
    setSelected(newDate);
    if (mode === "single") {
      props.onChange?.(newDate ?? null);
    }
    setIsOpen(false);
  };
  const handleRangeSelect = (currentRange, selectedDay, newRange) => {
    if (!currentRange?.from || currentRange.from && currentRange.to) {
      const newStart = { from: selectedDay, to: void 0 };
      setSelected(newStart);
      if (mode === "range") {
        props.onChange?.({ startDate: selectedDay, endDate: null });
      }
    } else {
      setSelected(newRange);
      if (mode === "range") {
        props.onChange?.(dateRangeToRangeValue(newRange));
      }
      if (newRange?.from && newRange?.to) {
        setIsOpen(false);
      }
    }
  };
  const handleSelect = (newSelection, selectedDay) => {
    if (mode === "single") {
      handleSingleSelect(newSelection);
    } else if (mode === "range") {
      handleRangeSelect(
        selected,
        selectedDay,
        newSelection
      );
    }
  };
  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(void 0);
    if (mode === "single") {
      props.onChange?.(null);
    } else {
      props.onChange?.({ startDate: null, endDate: null });
    }
    props.onClear?.();
    setIsOpen(false);
  };
  const handlePresetSelect = (range) => {
    if (mode === "single") {
      setSelected(range.to);
      props.onChange?.(range.to);
    } else if (mode === "range") {
      setSelected(range);
      props.onChange?.({ startDate: range.from, endDate: range.to });
    }
    setIsOpen(false);
  };
  const togglePicker = () => setIsOpen(!isOpen);
  const displayText = useMemo(() => {
    const defaultPlaceholder = mode === "single" ? "mm-dd-yyyy" : "mm-dd-yyyy / mm-dd-yyyy";
    const placeholderText = placeholder ?? defaultPlaceholder;
    if (mode === "single" && selected) {
      return formatDate(selected, format);
    }
    if (mode === "range" && selected) {
      const range = selected;
      if (range.from && range.to) {
        return `${formatDate(range.from, format)} - ${formatDate(range.to, format)}`;
      }
      if (range.from) {
        return `${formatDate(range.from, format)} - ${defaultPlaceholder.split(" - ")[1]}`;
      }
    }
    return placeholderText;
  }, [mode, selected, placeholder, format]);
  const commonDayPickerProps = {
    navLayout: "around",
    captionLayout: "dropdown",
    formatters: { formatWeekdayName },
    classNames: dayPickerStyles,
    numberOfMonths,
    disabled: disabled ? true : [...min ? [{ before: min }] : [], ...max ? [{ after: max }] : []],
    required
  };
  return /* @__PURE__ */ jsxs15("div", { className: `w-full ${className ?? ""}`, children: [
    label && /* @__PURE__ */ jsxs15(
      "label",
      {
        htmlFor: id,
        className: "block text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2",
        children: [
          label,
          required && /* @__PURE__ */ jsx26("span", { className: "text-red-500 ml-1", children: "*" })
        ]
      }
    ),
    /* @__PURE__ */ jsx26(
      Button,
      {
        ref: buttonRef,
        id,
        type: "button",
        variant: "outline",
        onClick: togglePicker,
        disabled,
        rightIcon: /* @__PURE__ */ jsxs15("div", { className: "flex items-end-safe space-x-2", children: [
          /* @__PURE__ */ jsx26(
            Icon,
            {
              name: "FiX",
              size: "md",
              className: `cursor-pointer hover:opacity-70 ${allowClear && selected ? "visible" : "invisible"}`,
              onClick: selected ? handleClear : void 0
            }
          ),
          /* @__PURE__ */ jsx26(
            Icon,
            {
              name: "HiCalendarDays",
              size: "md",
              className: "dark:fill-xtnd-white hover:opacity-70"
            }
          )
        ] }),
        className: `bg-xtnd-light-900 border rounded-md shadow-sm cursor-pointer sm:text-sm dark:bg-xtnd-dark-800 hover:bg-xtnd-white ${error ? "!border-red-600" : ""} ${buttonClassName ?? ""}`,
        children: /* @__PURE__ */ jsx26("span", { className: "inline-block min-w-[23ch] text-left truncate", children: displayText })
      }
    ),
    error && /* @__PURE__ */ jsx26("span", { className: "block text-sm font-medium text-red-600 mt-1", children: error }),
    isOpen && createPortal2(
      /* @__PURE__ */ jsxs15(Fragment4, { children: [
        /* @__PURE__ */ jsx26(
          "div",
          {
            className: "fixed inset-0 z-40",
            onClick: () => setIsOpen(false),
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx26(
          "div",
          {
            ref: calendarRef,
            className: `fixed z-50 ${mode === "range" ? "max-w-2xl" : "max-w-xs"}`,
            style: calendarStyles,
            children: mode === "single" ? /* @__PURE__ */ jsx26(
              DayPicker,
              {
                mode: "single",
                selected,
                onSelect: handleSelect,
                defaultMonth: selected,
                startMonth: new Date(startYear, 0),
                endMonth: new Date(endYear, 0),
                ...commonDayPickerProps
              }
            ) : /* @__PURE__ */ jsx26(
              DayPicker,
              {
                mode: "range",
                selected,
                onSelect: handleSelect,
                defaultMonth: selected?.from ?? /* @__PURE__ */ new Date(),
                startMonth: new Date(startYear, 0),
                endMonth: new Date(endYear, 0),
                footer: /* @__PURE__ */ jsx26(
                  DateRangePresets,
                  {
                    onPresetSelect: handlePresetSelect,
                    onClose: () => setIsOpen(false)
                  }
                ),
                ...commonDayPickerProps
              }
            )
          }
        )
      ] }),
      document.body
    )
  ] });
}
var DatePicker_default = DatePicker;

// src/components/ActionsMenu.tsx
import React15 from "react";
import { createPortal as createPortal3 } from "react-dom";
import { Fragment as Fragment5, jsx as jsx27, jsxs as jsxs16 } from "react/jsx-runtime";
var MENU_GAP = 8;
var ActionsMenu = React15.forwardRef(
  ({ isOpen, onClose, children, triggerElement }, ref) => {
    const menuRef = React15.useRef(null);
    const [menuStyles, setMenuStyles] = React15.useState({ top: 0, left: 0 });
    React15.useImperativeHandle(ref, () => menuRef.current);
    const updatePosition = React15.useCallback(() => {
      if (!triggerElement || !menuRef.current) return;
      const triggerRect = triggerElement.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const shouldOpenAbove = viewportHeight - triggerRect.bottom < menuRect.height + MENU_GAP;
      const top = shouldOpenAbove ? triggerRect.top - menuRect.height - MENU_GAP : triggerRect.bottom + MENU_GAP;
      const left = Math.min(
        Math.max(triggerRect.right - menuRect.width, MENU_GAP),
        viewportWidth - menuRect.width - MENU_GAP
      );
      setMenuStyles({
        top: Math.max(top, MENU_GAP),
        left
      });
    }, [triggerElement]);
    React15.useEffect(() => {
      if (!isOpen) return;
      const rAF = requestAnimationFrame(updatePosition);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        cancelAnimationFrame(rAF);
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }, [isOpen, updatePosition]);
    React15.useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === "Escape" && isOpen) {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose]);
    if (!isOpen || !triggerElement) return null;
    const menuContent = /* @__PURE__ */ jsxs16(Fragment5, { children: [
      /* @__PURE__ */ jsx27(
        "div",
        {
          role: "button",
          tabIndex: 0,
          className: "fixed inset-0 z-40",
          onClick: onClose,
          onKeyDown: (e) => {
            if (e.key === "Escape" || e.key === "Enter") {
              onClose();
            }
          },
          "aria-label": "Close menu"
        }
      ),
      /* @__PURE__ */ jsx27(
        "div",
        {
          ref: menuRef,
          className: "fixed z-50 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 max-h-[400px] overflow-y-auto",
          style: menuStyles,
          children
        }
      )
    ] });
    return createPortal3(menuContent, document.body);
  }
);
ActionsMenu.displayName = "ActionsMenu";
var ActionsMenuItem = React15.forwardRef(
  ({ icon, iconColor = "text-xtnd-blue", label, disabled = false, className = "", ...restProps }, ref) => {
    const baseClasses = "w-full px-4 py-2 text-left text-sm flex items-center space-x-3";
    const stateClasses = disabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed" : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700";
    const classes = `${baseClasses} ${stateClasses} ${className}`.trim();
    return /* @__PURE__ */ jsxs16("button", { ref, disabled, className: classes, ...restProps, children: [
      icon && /* @__PURE__ */ jsx27(
        Icon,
        {
          name: icon,
          className: `h-4 w-4 ${disabled ? "text-gray-400" : iconColor}`
        }
      ),
      /* @__PURE__ */ jsx27("span", { children: label })
    ] });
  }
);
ActionsMenuItem.displayName = "ActionsMenuItem";
var ActionsMenuDivider = React15.forwardRef(
  ({ className = "", ...restProps }, ref) => {
    const classes = `border-t border-gray-200 dark:border-gray-700 my-1 ${className}`.trim();
    return /* @__PURE__ */ jsx27("div", { ref, className: classes, ...restProps });
  }
);
ActionsMenuDivider.displayName = "ActionsMenuDivider";

// src/components/Skeleton.tsx
import { jsx as jsx28, jsxs as jsxs17 } from "react/jsx-runtime";
var Skeleton = ({
  className = "",
  variant = "text",
  width,
  height,
  lines = 1
}) => {
  const baseClass = "animate-pulse bg-gray-200 dark:bg-gray-700";
  const variantClass = {
    text: "rounded",
    circular: "rounded-full",
    rectangular: "rounded-lg"
  }[variant];
  const style = {
    width: width || (variant === "circular" ? "40px" : "100%"),
    height: height || (variant === "text" ? "1rem" : variant === "circular" ? "40px" : "200px")
  };
  if (variant === "text" && lines > 1) {
    return /* @__PURE__ */ jsx28("div", { className: `space-y-2 ${className}`, children: Array.from({ length: lines }).map((_, index) => /* @__PURE__ */ jsx28(
      "div",
      {
        className: `${baseClass} ${variantClass}`,
        style: {
          ...style,
          width: index === lines - 1 ? "80%" : style.width
        }
      },
      index
    )) });
  }
  return /* @__PURE__ */ jsx28(
    "div",
    {
      className: `${baseClass} ${variantClass} ${className}`,
      style
    }
  );
};
var SkeletonCard = ({ className = "" }) => /* @__PURE__ */ jsx28("div", { className: `bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 ${className}`, children: /* @__PURE__ */ jsxs17("div", { className: "flex items-start space-x-4", children: [
  /* @__PURE__ */ jsx28(Skeleton, { variant: "circular", width: 48, height: 48 }),
  /* @__PURE__ */ jsxs17("div", { className: "flex-1", children: [
    /* @__PURE__ */ jsx28(Skeleton, { variant: "text", width: "60%", height: "1.25rem", className: "mb-3" }),
    /* @__PURE__ */ jsx28(Skeleton, { variant: "text", lines: 2 })
  ] })
] }) });
var SkeletonTable = ({
  rows = 5,
  columns = 4,
  className = ""
}) => /* @__PURE__ */ jsxs17("div", { className: `bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 ${className}`, children: [
  /* @__PURE__ */ jsx28("div", { className: "border-b border-gray-200 dark:border-gray-700 p-4", children: /* @__PURE__ */ jsx28("div", { className: "grid gap-4", style: { gridTemplateColumns: `repeat(${columns}, 1fr)` }, children: Array.from({ length: columns }).map((_, index) => /* @__PURE__ */ jsx28(Skeleton, { variant: "text", width: "80%", height: "1rem" }, index)) }) }),
  Array.from({ length: rows }).map((_, rowIndex) => /* @__PURE__ */ jsx28(
    "div",
    {
      className: "border-b border-gray-200 dark:border-gray-700 last:border-0 p-4",
      children: /* @__PURE__ */ jsx28("div", { className: "grid gap-4", style: { gridTemplateColumns: `repeat(${columns}, 1fr)` }, children: Array.from({ length: columns }).map((_2, colIndex) => /* @__PURE__ */ jsx28(Skeleton, { variant: "text", width: "90%" }, colIndex)) })
    },
    rowIndex
  ))
] });
var SkeletonForm = ({
  fields = 3,
  className = ""
}) => /* @__PURE__ */ jsx28("div", { className: `space-y-4 ${className}`, children: Array.from({ length: fields }).map((_, index) => /* @__PURE__ */ jsxs17("div", { children: [
  /* @__PURE__ */ jsx28(Skeleton, { variant: "text", width: "30%", height: "1rem", className: "mb-2" }),
  /* @__PURE__ */ jsx28(Skeleton, { variant: "rectangular", height: "2.5rem" })
] }, index)) });

// src/components/SlideOutPanel.tsx
import React16 from "react";
import { jsx as jsx29, jsxs as jsxs18 } from "react/jsx-runtime";
var SlideOutPanelA11yContext = React16.createContext(null);
var SlideOutPanel = React16.forwardRef(
  ({
    open,
    onClose,
    title,
    closeOnBackdrop = true,
    closeOnEscape = true,
    showCloseButton = true,
    ariaLabel,
    children,
    width = "lg",
    className = "",
    ...props
  }, ref) => {
    const panelRef = React16.useRef(null);
    const overlayRef = React16.useRef(null);
    const previouslyFocusedRef = React16.useRef(null);
    const generatedHeaderId = React16.useId();
    const headerId = generatedHeaderId;
    React16.useImperativeHandle(ref, () => panelRef.current);
    React16.useEffect(() => {
      if (!closeOnEscape) return;
      const handleEscape = (e) => {
        if (e.key === "Escape" && open) {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onClose, closeOnEscape]);
    React16.useEffect(() => {
      if (open) {
        previouslyFocusedRef.current = document.activeElement;
        document.body.style.overflow = "hidden";
        setTimeout(() => {
          if (!panelRef.current) return;
          const focusable = panelRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) {
            focusable[0]?.focus();
          } else {
            panelRef.current.focus();
          }
        }, 0);
      } else {
        document.body.style.overflow = "unset";
        if (previouslyFocusedRef.current) {
          previouslyFocusedRef.current.focus();
        }
      }
      return () => {
        document.body.style.overflow = "unset";
        if (previouslyFocusedRef.current) {
          previouslyFocusedRef.current.focus();
        }
      };
    }, [open]);
    const handleBackdropClick = (e) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose();
    };
    const handleBackdropKeyDown = (e) => {
      if (!closeOnBackdrop) return;
      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        e.preventDefault();
        onClose();
      }
    };
    React16.useEffect(() => {
      if (!open || !panelRef.current) return;
      const element = panelRef.current;
      const handler = (e) => {
        if (e.key !== "Tab") return;
        const focusable = element.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !active || !element.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      };
      element.addEventListener("keydown", handler);
      return () => element.removeEventListener("keydown", handler);
    }, [open]);
    if (!open) return null;
    const widthClasses = {
      sm: "w-96",
      md: "w-[500px]",
      lg: "w-[700px]",
      xl: "w-[900px]",
      full: "w-[calc(100vw-2rem)]"
    };
    return /* @__PURE__ */ jsx29("div", { ref: overlayRef, className: "fixed inset-0 z-50", children: /* @__PURE__ */ jsxs18(
      "div",
      {
        className: "flex h-full w-full items-start justify-end",
        role: "button",
        "aria-label": "Close slide-out panel by clicking backdrop",
        tabIndex: 0,
        onClick: handleBackdropClick,
        onKeyDown: handleBackdropKeyDown,
        children: [
          /* @__PURE__ */ jsx29("div", { className: "fixed inset-0 bg-black/40 transition-opacity" }),
          /* @__PURE__ */ jsx29(SlideOutPanelA11yContext.Provider, { value: { headerId }, children: /* @__PURE__ */ jsxs18(
            "div",
            {
              ref: panelRef,
              className: `relative border-l border-gray-200 bg-white rounded-l-lg shadow-xl h-full transform transition-all duration-300 ease-in-out translate-x-0 dark:bg-xtnd-dark-800 dark:text-xtnd-white dark:border-xtnd-dark-600 ${widthClasses[width]} ${className}`.trim(),
              role: "dialog",
              "aria-modal": "true",
              "aria-label": ariaLabel || title,
              "aria-labelledby": ariaLabel || title ? void 0 : headerId,
              tabIndex: -1,
              ...props,
              children: [
                (title || showCloseButton) && /* @__PURE__ */ jsxs18("div", { className: "flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600", children: [
                  title && /* @__PURE__ */ jsx29("h2", { className: "text-xl font-semibold text-xtnd-dark dark:text-xtnd-white", children: title }),
                  showCloseButton && /* @__PURE__ */ jsx29(
                    "button",
                    {
                      type: "button",
                      className: "inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700",
                      onClick: onClose,
                      "aria-label": "Close slide-out panel",
                      children: /* @__PURE__ */ jsx29(Icon, { name: "HiOutlineX" })
                    }
                  )
                ] }),
                children
              ]
            }
          ) })
        ]
      }
    ) });
  }
);
var SlideOutPanelHeader = React16.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600 ${className}`.trim();
    return /* @__PURE__ */ jsx29("div", { ref, className: classes, ...props, children });
  }
);
var SlideOutPanelBody = React16.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 flex-1 overflow-y-auto ${className}`.trim();
    return /* @__PURE__ */ jsx29("div", { ref, className: classes, ...props, children });
  }
);
var SlideOutPanelFooter = React16.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-t border-gray-200 dark:border-xtnd-dark-600 flex justify-end space-x-2 ${className}`.trim();
    return /* @__PURE__ */ jsx29("div", { ref, className: classes, ...props, children });
  }
);
SlideOutPanel.displayName = "SlideOutPanel";
SlideOutPanelHeader.displayName = "SlideOutPanelHeader";
SlideOutPanelBody.displayName = "SlideOutPanelBody";
SlideOutPanelFooter.displayName = "SlideOutPanelFooter";
var SlideOutPanel_default = SlideOutPanel;

// src/components/Drawer.tsx
import React17 from "react";
import { jsx as jsx30, jsxs as jsxs19 } from "react/jsx-runtime";
var DrawerA11yContext = React17.createContext(null);
var Drawer = React17.forwardRef(
  ({
    open,
    onClose,
    title,
    closeOnBackdrop = true,
    closeOnEscape = true,
    showCloseButton = true,
    ariaLabel,
    children,
    height = "md",
    className = "",
    position = "bottom",
    width = "",
    ...props
  }, ref) => {
    const drawerRef = React17.useRef(null);
    const overlayRef = React17.useRef(null);
    const previouslyFocusedRef = React17.useRef(null);
    const generatedHeaderId = React17.useId();
    const headerId = generatedHeaderId;
    const [isVisible, setIsVisible] = React17.useState(false);
    const [shouldRender, setShouldRender] = React17.useState(false);
    React17.useImperativeHandle(ref, () => drawerRef.current);
    React17.useEffect(() => {
      if (open) {
        setShouldRender(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        });
      } else {
        setIsVisible(false);
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [open]);
    React17.useEffect(() => {
      if (!closeOnEscape) return;
      const handleEscape = (e) => {
        if (e.key === "Escape" && isVisible) {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isVisible, onClose, closeOnEscape]);
    React17.useEffect(() => {
      if (isVisible) {
        previouslyFocusedRef.current = document.activeElement;
        document.body.style.overflow = "hidden";
        setTimeout(() => {
          if (!drawerRef.current) return;
          const focusable = drawerRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) {
            focusable[0]?.focus();
          } else {
            drawerRef.current.focus();
          }
        }, 100);
      } else {
        document.body.style.overflow = "unset";
        const timer = setTimeout(() => {
          if (previouslyFocusedRef.current) {
            previouslyFocusedRef.current.focus();
          }
        }, 300);
        return () => clearTimeout(timer);
      }
      return () => {
        document.body.style.overflow = "unset";
        if (previouslyFocusedRef.current) {
          previouslyFocusedRef.current.focus();
        }
      };
    }, [isVisible]);
    const handleBackdropClick = (e) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose();
    };
    const handleBackdropKeyDown = (e) => {
      if (!closeOnBackdrop) return;
      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        e.preventDefault();
        onClose();
      }
    };
    React17.useEffect(() => {
      if (!isVisible || !drawerRef.current) return;
      const element = drawerRef.current;
      const handler = (e) => {
        if (e.key !== "Tab") return;
        const focusable = element.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !active || !element.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      };
      element.addEventListener("keydown", handler);
      return () => element.removeEventListener("keydown", handler);
    }, [isVisible]);
    if (!shouldRender) return null;
    const heightClasses = {
      sm: "max-h-[40vh]",
      md: "max-h-[60vh]",
      lg: "max-h-[80vh]",
      xl: "max-h-[90vh]",
      full: "max-h-[95vh]"
    };
    const baseDrawerClass = `bg-white shadow-xl transform transition-all duration-300 will-change-transform
    opacity-100 ease-[cubic-bezier(0.32,0.72,0,1)] dark:bg-xtnd-dark-800 dark:text-xtnd-white
    dark:border-xtnd-dark-600 flex flex-col cursor-default`;
    const positionConfig = {
      bottom: `relative w-full min-h-[95vh] ${heightClasses[height]} border-t border-gray-200 rounded-t-lg translate-y-0`,
      top: `top-0 left-0 right-0 relative w-full ${heightClasses[height]} border-b border-gray-200 rounded-b-lg translate-y-100`,
      left: `fixed top-0 left-0 h-full ${width} border-r border-gray-200 rounded-r-lg translate-x-0`,
      right: `fixed top-0 right-0 h-full ${width} border-l border-gray-200 rounded-l-lg translate-x-0`
    };
    const hiddenTransform = {
      top: "-translate-y-full opacity-0",
      bottom: "translate-y-full opacity-0",
      left: "-translate-x-full opacity-0",
      right: "translate-x-full opacity-0"
    };
    const drawerClass = `${baseDrawerClass} ${positionConfig[position] || positionConfig["bottom"]} ${isVisible ? "" : hiddenTransform[position]} ${className}`.trim();
    return /* @__PURE__ */ jsx30("div", { ref: overlayRef, className: "fixed inset-0 z-50", children: /* @__PURE__ */ jsxs19(
      "div",
      {
        className: "flex h-full w-full items-end justify-center",
        role: "button",
        "aria-label": "Close drawer by clicking backdrop",
        tabIndex: 0,
        onClick: handleBackdropClick,
        onKeyDown: handleBackdropKeyDown,
        children: [
          /* @__PURE__ */ jsx30(
            "div",
            {
              onClick: onClose,
              className: `fixed inset-0 bg-black/60 transition-opacity duration-300 ease-in-out will-change-opacity ${isVisible ? "opacity-100" : "opacity-0"}`
            }
          ),
          /* @__PURE__ */ jsx30(DrawerA11yContext.Provider, { value: { headerId }, children: /* @__PURE__ */ jsxs19(
            "div",
            {
              ref: drawerRef,
              className: drawerClass,
              role: "dialog",
              "aria-modal": "true",
              "aria-label": ariaLabel || title,
              "aria-labelledby": ariaLabel || title ? void 0 : headerId,
              tabIndex: -1,
              ...props,
              children: [
                position === "bottom" && /* @__PURE__ */ jsx30(
                  "div",
                  {
                    className: `flex justify-center pt-2 pb-1 transition-opacity duration-300 ${isVisible ? "opacity-100 delay-75" : "opacity-0"}`,
                    children: /* @__PURE__ */ jsx30("div", { className: "w-12 h-1 bg-gray-300 dark:bg-xtnd-dark-600 rounded-full" })
                  }
                ),
                (title || showCloseButton) && /* @__PURE__ */ jsxs19("div", { className: "flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600 flex-shrink-0", children: [
                  title && /* @__PURE__ */ jsx30("h2", { className: "text-xl font-semibold text-xtnd-dark dark:text-xtnd-white", children: title }),
                  showCloseButton && /* @__PURE__ */ jsx30(
                    "button",
                    {
                      type: "button",
                      className: "inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700",
                      onClick: onClose,
                      "aria-label": "Close drawer",
                      children: /* @__PURE__ */ jsx30(Icon, { name: "HiOutlineX" })
                    }
                  )
                ] }),
                children
              ]
            }
          ) })
        ]
      }
    ) });
  }
);
var DrawerHeader = React17.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600 flex-shrink-0 ${className}`.trim();
    const a11y = React17.useContext(DrawerA11yContext);
    return /* @__PURE__ */ jsx30("div", { ref, className: classes, id: a11y?.headerId, ...props, children });
  }
);
var DrawerBody = React17.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 flex-1 overflow-y-auto ${className}`.trim();
    return /* @__PURE__ */ jsx30("div", { ref, className: classes, ...props, children });
  }
);
var DrawerFooter = React17.forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const classes = `px-6 py-4 border-t border-gray-200 dark:border-xtnd-dark-600 flex justify-end space-x-2 flex-shrink-0 ${className}`.trim();
    return /* @__PURE__ */ jsx30("div", { ref, className: classes, ...props, children });
  }
);
Drawer.displayName = "Drawer";
DrawerHeader.displayName = "DrawerHeader";
DrawerBody.displayName = "DrawerBody";
DrawerFooter.displayName = "DrawerFooter";
var Drawer_default = Drawer;

// src/components/EmptyState.tsx
import React18 from "react";
import { jsx as jsx31, jsxs as jsxs20 } from "react/jsx-runtime";
var EmptyState = React18.forwardRef(
  ({
    icon,
    title,
    description,
    helpText,
    variant = "outlined",
    padding = "lg",
    titleSize = "md",
    ...props
  }, ref) => {
    const titleClasses = {
      sm: "text-lg font-medium",
      md: "text-xl font-semibold",
      lg: "text-2xl font-bold"
    };
    return /* @__PURE__ */ jsx31(Card, { variant, padding, ref, ...props, children: /* @__PURE__ */ jsxs20(CardBody, { className: "text-center py-12", children: [
      /* @__PURE__ */ jsx31(Icon, { name: icon, className: "h-16 w-16 text-gray-400 mx-auto mb-4" }),
      /* @__PURE__ */ jsx31(
        "h2",
        {
          className: `${titleClasses[titleSize]} text-xtnd-dark dark:text-xtnd-white mb-2`,
          children: title
        }
      ),
      /* @__PURE__ */ jsx31("p", { className: "text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto", children: description }),
      helpText && /* @__PURE__ */ jsx31("p", { className: "text-sm text-gray-500 dark:text-gray-500", children: helpText })
    ] }) });
  }
);
EmptyState.displayName = "EmptyState";
var EmptyState_default = EmptyState;

// src/components/toast.ts
import { toast as reactToast } from "react-toastify";
var toast = {
  success: (message) => reactToast.success(message),
  error: (message) => reactToast.error(message),
  warning: (message) => reactToast.warning(message),
  info: (message) => reactToast.info(message),
  promise: async (promise, options) => {
    return reactToast.promise(promise, options);
  }
};
var showToast = toast;

// src/components/FileUpload.tsx
import { useMemo as useMemo2, useRef as useRef4, useState as useState4 } from "react";
import { HiOutlineExclamationCircle as HiOutlineExclamationCircle4 } from "react-icons/hi";
import { jsx as jsx32, jsxs as jsxs21 } from "react/jsx-runtime";
var FileUpload = ({
  id,
  isMultiple = false,
  accept = ".pdf,.jpg,.jpeg,.png,.txt,.tif",
  maxSize = 10 * 1024 * 1024,
  // 10MB default
  maxFileNumber = 6,
  disabled = false,
  className,
  files = [],
  onChange = () => {
  },
  error,
  setError
}) => {
  const [isDragging, setIsDragging] = useState4(false);
  const [internalError, setInternalError] = useState4("");
  const fileInputRef = useRef4(null);
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };
  const isAccepted = (file) => {
    if (!accept || accept === ".*" || accept === "*") return true;
    const rules = accept.split(",").map((s) => s.trim()).filter(Boolean);
    const name = file.name.toLowerCase();
    const type = (file.type || "").toLowerCase();
    return rules.some((rule) => {
      const r = rule.toLowerCase();
      if (r.startsWith(".")) return name.endsWith(r);
      if (r.endsWith("/*")) return type.startsWith(r.slice(0, -1));
      return type === r;
    });
  };
  const validateFile = (file) => {
    if (!isAccepted(file)) {
      return `Can't upload ${file.name}. This file type isn't supported.`;
    }
    if (file.size > maxSize) {
      return `Can't upload ${file.name}. File exceeds the ${formatFileSize(maxSize)} limit.`;
    }
    return null;
  };
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const updateError = (message) => {
    setInternalError(message);
    if (setError && typeof setError === "function") {
      setError(message);
    }
  };
  const handleFiles = (fileList) => {
    if (disabled) return;
    const newFiles = Array.from(fileList);
    if (maxFileNumber !== void 0 && isMultiple) {
      const totalFiles = files.length + newFiles.length;
      if (totalFiles > maxFileNumber) {
        updateError(
          `Maximum ${maxFileNumber} file${maxFileNumber > 1 ? "s" : ""} allowed. You are trying to add ${newFiles.length} file${newFiles.length > 1 ? "s" : ""} but only ${Math.max(0, maxFileNumber - files.length)} slot${maxFileNumber - files.length !== 1 ? "s" : ""} remaining.`
        );
        resetFileInput();
        return;
      }
    }
    const accepted = [];
    const errors = [];
    for (const file of newFiles) {
      const err = validateFile(file);
      if (err) errors.push(err);
      else accepted.push(file);
    }
    updateError(errors.join("\n"));
    if (accepted.length > 0) {
      const updatedFiles = isMultiple ? [...files, ...accepted] : accepted;
      onChange(updatedFiles);
    }
    resetFileInput();
  };
  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };
  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };
  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
  };
  const removeFile = (index) => {
    if (disabled) return;
    const updatedFiles = files.filter((_, i) => i !== index);
    if (updatedFiles?.length < (maxFileNumber ?? 0)) {
      updateError("");
    }
    onChange(updatedFiles);
  };
  const getSupportedTypes = () => {
    return accept.replace(/\./g, "").split(",").join(", ");
  };
  const title = useMemo2(() => {
    if (isMultiple) {
      return "Drop your files here!";
    }
    return "Drop your file here!";
  }, [isMultiple]);
  const displayError = internalError || error;
  const hasError = Boolean(displayError);
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      className: `w-full max-w-2xl max-h-[90vh] overflow-y-auto ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className || ""}`,
      children: [
        /* @__PURE__ */ jsxs21(
          "div",
          {
            className: `border-2 border-dashed rounded-lg transition-colors ${disabled ? "border-xtnd-dark-200 bg-xtnd-dark-50 dark:border-xtnd-dark-600 dark:bg-xtnd-dark-900 cursor-not-allowed" : hasError ? "border-red-400 bg-red-50 dark:border-red-500 dark:bg-red-900" : isDragging ? "border-xtnd-blue dark:border-xtnd-blue dark:bg-xtnd-dark-700" : "border-gray-300 bg-xtnd-white hover:border-xtnd-dark-200 dark:border-xtnd-dark-500 dark:bg-xtnd-dark-800 dark:hover:border-xtnd-dark-400"}`,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
            children: [
              /* @__PURE__ */ jsxs21("div", { className: "p-12 text-center", children: [
                /* @__PURE__ */ jsxs21("div", { className: "flex flex-col items-center space-y-3", children: [
                  /* @__PURE__ */ jsx32("div", { className: "text-xtnd-dark dark:text-xtnd-light text-md font-medium", children: title }),
                  /* @__PURE__ */ jsx32(
                    "button",
                    {
                      onClick: handleClick,
                      disabled,
                      type: "button",
                      className: `text-sm underline ${disabled ? "text-xtnd-dark-300 dark:text-xtnd-dark-500 cursor-not-allowed" : "text-xtnd-blue hover:text-xtnd-dark dark:text-xtnd-light dark:hover:text-xtnd-blue"}`,
                      children: "Or click here"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx32(
                  "input",
                  {
                    id,
                    ref: fileInputRef,
                    type: "file",
                    className: "hidden",
                    accept,
                    multiple: isMultiple,
                    disabled,
                    onChange: handleFileChange
                  }
                )
              ] }),
              files.length > 0 && /* @__PURE__ */ jsx32("div", { className: "px-6 pb-6 space-y-3", children: files.map((file, index) => /* @__PURE__ */ jsxs21(
                "div",
                {
                  className: "flex text-sm items-center justify-between bg-xtnd-light dark:bg-xtnd-dark-700 rounded-md",
                  children: [
                    /* @__PURE__ */ jsx32("div", { className: "flex-1 text-sm font-medium text-xtnd-dark dark:text-xtnd-light px-2 max-w-md truncate", children: file.name }),
                    /* @__PURE__ */ jsx32(
                      Button_default,
                      {
                        className: "ml-4 flex items-center justify-center w-10 h-10 rounded-md transition-colors",
                        variant: "primary",
                        type: "button",
                        onClick: () => removeFile(index),
                        size: "sm",
                        disabled,
                        children: /* @__PURE__ */ jsx32(Icon, { name: "HiTrash", size: "sm" })
                      }
                    )
                  ]
                },
                index
              )) })
            ]
          }
        ),
        displayError && /* @__PURE__ */ jsxs21("div", { className: "flex mt-1 items-start", children: [
          /* @__PURE__ */ jsx32(
            HiOutlineExclamationCircle4,
            {
              className: "h-4 w-4 text-red-500 mr-1 mt-0.5 flex-shrink-0",
              "aria-hidden": "true"
            }
          ),
          /* @__PURE__ */ jsx32("span", { role: "alert", className: "text-sm text-red-600 dark:text-red-400 whitespace-pre-line", children: displayError })
        ] }),
        /* @__PURE__ */ jsxs21("div", { className: "mt-2 text-xs text-xtnd-dark-500 dark:text-xtnd-light text-center", children: [
          "File types supported: ",
          getSupportedTypes(),
          maxFileNumber !== void 0 && isMultiple && /* @__PURE__ */ jsxs21("span", { className: "block mt-1", children: [
            "Maximum ",
            maxFileNumber,
            " file",
            maxFileNumber > 1 ? "s" : "",
            " allowed",
            files.length > 0 && ` (${files.length}/${maxFileNumber} selected)`
          ] }),
          maxSize !== void 0 && /* @__PURE__ */ jsxs21("span", { className: "block mt-1", children: [
            "Maximum file size: ",
            formatFileSize(maxSize)
          ] })
        ] })
      ]
    }
  );
};
var FileUpload_default = FileUpload;

// src/components/UserBadge.tsx
import React20 from "react";
import { jsx as jsx33, jsxs as jsxs22 } from "react/jsx-runtime";
var UserBadge = React20.forwardRef(
  ({
    src,
    name = "User",
    size = "md",
    showStatus = false,
    statusColor = "bg-green-500",
    className = ""
  }, ref) => {
    const sizeClasses3 = {
      xs: "h-6 w-6 text-[10px]",
      sm: "h-8 w-8 text-xs",
      md: "h-10 w-10 text-sm",
      lg: "h-12 w-12 text-base",
      xl: "h-14 w-14 text-lg"
    };
    const statusSizeClasses = {
      xs: "h-2 w-2 -bottom-0 -right-0",
      sm: "h-2.5 w-2.5 -bottom-0 -right-0",
      md: "h-3 w-3 -bottom-0.5 -right-0.5",
      lg: "h-3.5 w-3.5 -bottom-0.5 -right-0.5",
      xl: "h-4 w-4 -bottom-1 -right-1"
    };
    const initials = name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U";
    const isAbsolute = className.includes("absolute");
    return /* @__PURE__ */ jsxs22(
      "div",
      {
        ref,
        className: `${!isAbsolute ? "relative" : ""} inline-flex shrink-0 ${className}`,
        children: [
          src ? /* @__PURE__ */ jsx33(
            "img",
            {
              src,
              alt: name,
              className: `${sizeClasses3[size].split(" ")[0]} ${sizeClasses3[size].split(" ")[1]} rounded-full ring-2 ring-xtnd-blue/20 object-cover`
            }
          ) : /* @__PURE__ */ jsx33(
            "div",
            {
              className: `${sizeClasses3[size]} rounded-full bg-gradient-to-br from-xtnd-blue to-xtnd-blue-600 text-xtnd-white flex items-center justify-center font-bold shadow-lg`,
              children: initials
            }
          ),
          showStatus && /* @__PURE__ */ jsx33(
            "div",
            {
              className: `absolute ${statusSizeClasses[size]} ${statusColor} rounded-full border-2 border-xtnd-white dark:border-xtnd-dark`
            }
          )
        ]
      }
    );
  }
);
UserBadge.displayName = "UserBadge";

// src/components/Radio.tsx
import React21 from "react";
import { jsx as jsx34, jsxs as jsxs23 } from "react/jsx-runtime";
var RadioGroupContext = React21.createContext(null);
var RadioGroup = React21.forwardRef(
  ({
    value,
    defaultValue,
    onValueChange,
    name,
    disabled = false,
    required = false,
    orientation = "vertical",
    className = "",
    children,
    ...props
  }, ref) => {
    const isControlled = value !== void 0;
    const [uncontrolledValue, setUncontrolledValue] = React21.useState(
      defaultValue
    );
    const currentValue = isControlled ? value : uncontrolledValue;
    const handleValueChange = React21.useCallback(
      (nextValue) => {
        if (!isControlled) {
          setUncontrolledValue(nextValue);
        }
        onValueChange?.(nextValue);
      },
      [isControlled, onValueChange]
    );
    const orientationClasses = orientation === "horizontal" ? "flex flex-row gap-4" : "flex flex-col gap-3";
    const groupClasses = `inline-flex ${orientationClasses} ${className}`.trim();
    const contextValue = React21.useMemo(
      () => ({
        name,
        value: currentValue,
        disabled,
        required,
        onValueChange: handleValueChange
      }),
      [name, currentValue, disabled, required, handleValueChange]
    );
    return /* @__PURE__ */ jsx34(RadioGroupContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx34(
      "div",
      {
        ref,
        role: "radiogroup",
        "aria-disabled": disabled || void 0,
        "aria-orientation": orientation,
        className: groupClasses,
        ...props,
        children
      }
    ) });
  }
);
var sizeClasses2 = {
  sm: {
    control: "h-3.5 w-3.5",
    label: "text-xs",
    description: "text-[10px]"
  },
  md: {
    control: "h-4 w-4",
    label: "text-sm",
    description: "text-xs"
  },
  lg: {
    control: "h-5 w-5",
    label: "text-base",
    description: "text-sm"
  }
};
var Radio = React21.forwardRef(
  ({
    value,
    label,
    description,
    size = "md",
    error = false,
    errorMessage,
    required = false,
    disabled: disabledProp = false,
    className = "",
    containerClassName = "",
    onChange,
    id,
    ...props
  }, ref) => {
    const group = React21.useContext(RadioGroupContext);
    const inputId = React21.useId();
    const resolvedId = id ?? inputId;
    const descriptionId = description ? `${resolvedId}-description` : void 0;
    const errorId = error ? `${resolvedId}-error` : void 0;
    const isDisabled = disabledProp || group?.disabled;
    const isRequired = required || group?.required;
    const isChecked = group?.value !== void 0 ? group.value === value : props.checked;
    const handleChange = (event) => {
      if (group?.onValueChange) {
        group.onValueChange(value);
      }
      onChange?.(event);
    };
    const controlSize = sizeClasses2[size].control;
    const labelSize = sizeClasses2[size].label;
    const descriptionSize = sizeClasses2[size].description;
    const baseControlClasses = `
      inline-flex items-center justify-center rounded-full border border-gray-300
      bg-white dark:bg-xtnd-dark-800 dark:border-gray-600
      transition-colors
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-xtnd-blue focus-visible:ring-offset-2
      focus-visible:ring-offset-white dark:focus-visible:ring-offset-xtnd-dark
      ${controlSize}
    `;
    const checkedClasses = `
      ${isChecked ? "border-xtnd-blue" : "border-gray-300 dark:border-gray-600"}
    `;
    const containerClasses = `
      flex items-start space-x-3
      ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
      ${containerClassName}
    `;
    const inputClasses = `
      sr-only
    `;
    const controlWrapperClasses = `
      flex items-center pt-0.5
    `;
    const textWrapperClasses = `
      flex-1
    `;
    const labelClasses = `
      font-medium text-gray-700 dark:text-gray-300
      ${labelSize}
    `;
    const descriptionClasses = `
      text-gray-500 dark:text-gray-400 mt-0.5
      ${descriptionSize}
    `;
    const errorTextClasses = `
      text-red-500 dark:text-red-400 text-xs mt-1 ml-6
    `;
    const controlClasses = `${baseControlClasses} ${checkedClasses} ${className}`.trim();
    const radioControl = /* @__PURE__ */ jsx34("span", { className: controlWrapperClasses.trim(), children: /* @__PURE__ */ jsx34("span", { className: controlClasses, children: /* @__PURE__ */ jsx34(
      "span",
      {
        className: `
              inline-block rounded-full bg-xtnd-blue
              transition-opacity
              ${isChecked ? "opacity-100" : "opacity-0"}
              ${size === "sm" ? "h-2 w-2" : size === "md" ? "h-2.5 w-2.5" : "h-3 w-3"}
            `.trim()
      }
    ) }) });
    const radioInput = /* @__PURE__ */ jsx34(
      "input",
      {
        ref,
        id: resolvedId,
        type: "radio",
        "aria-checked": !!isChecked,
        "aria-disabled": isDisabled || void 0,
        "aria-describedby": [descriptionId, errorId].filter(Boolean).length ? [descriptionId, errorId].filter(Boolean).join(" ") : void 0,
        disabled: isDisabled,
        required: isRequired,
        name: group?.name ?? props.name,
        value,
        checked: !!isChecked,
        onChange: handleChange,
        className: inputClasses.trim(),
        ...props
      }
    );
    if (!label && !description) {
      return /* @__PURE__ */ jsxs23("label", { className: containerClasses.trim(), children: [
        radioInput,
        radioControl
      ] });
    }
    return /* @__PURE__ */ jsxs23("div", { className: containerClassName, children: [
      /* @__PURE__ */ jsxs23("label", { className: containerClasses.trim(), htmlFor: resolvedId, children: [
        radioInput,
        radioControl,
        /* @__PURE__ */ jsxs23("div", { className: textWrapperClasses.trim(), children: [
          label && /* @__PURE__ */ jsxs23("div", { className: labelClasses.trim(), children: [
            label,
            isRequired && /* @__PURE__ */ jsx34("span", { className: "text-red-500 ml-1", "aria-label": "required", children: "*" })
          ] }),
          description && /* @__PURE__ */ jsx34("p", { id: descriptionId, className: descriptionClasses.trim(), children: description })
        ] })
      ] }),
      error && errorMessage && /* @__PURE__ */ jsx34("p", { id: errorId, className: errorTextClasses.trim(), children: errorMessage })
    ] });
  }
);
RadioGroup.displayName = "RadioGroup";
Radio.displayName = "Radio";

// src/components/ClioLinkButton.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var ClioLinkButton = ({
  id,
  modal = "matters",
  className = ""
}) => {
  const handleOpen = () => {
    window.open(
      `https://app.clio.com/${modal}/${id}`,
      "clio-tab"
    );
  };
  return /* @__PURE__ */ jsx35(
    "button",
    {
      type: "button",
      onClick: handleOpen,
      "aria-label": "Open in Clio",
      className: `
        flex items-center gap-1
        text-xtnd-blue hover:text-xtnd-blue-600
        dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200
        hover:underline
        focus:outline-none focus:ring-2 focus:ring-xtnd-blue
        rounded transition-colors
        ${className}
      `,
      children: /* @__PURE__ */ jsx35(
        Icon,
        {
          name: "HiExternalLink",
          className: "h-3.5 w-3.5 flex-shrink-0"
        }
      )
    }
  );
};
var ClioLinkButton_default = ClioLinkButton;

// src/components/Workflow.tsx
import { jsx as jsx36, jsxs as jsxs24 } from "react/jsx-runtime";
var getStepStyle = (step) => {
  switch (step) {
    case 1:
      return "bg-red-100 text-red-600";
    case 2:
      return "bg-yellow-100 text-yellow-600";
    case 3:
      return "bg-blue-100 text-blue-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};
var Workflow = ({ label, steps }) => {
  return /* @__PURE__ */ jsxs24("div", { className: "rounded-xl border border-yellow-200 bg-yellow-50 px-6 py-4 dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white", children: [
    /* @__PURE__ */ jsx36("div", { className: "mb-4 flex items-center gap-2 font-medium text-yellow-700", children: label }),
    /* @__PURE__ */ jsx36("div", { className: "flex flex-wrap items-center gap-x-6 gap-y-3", children: steps.map((step, idx) => /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2 whitespace-nowrap", children: [
      /* @__PURE__ */ jsx36(
        "span",
        {
          className: `
                flex h-7 w-7 shrink-0 items-center justify-center
                rounded-full text-sm font-semibold
                ${getStepStyle(step.index)}
              `,
          children: step.index
        }
      ),
      /* @__PURE__ */ jsx36("span", { className: "text-sm font-medium text-gray-800 dark:text-xtnd-light", children: step.label }),
      idx < steps.length - 1 && /* @__PURE__ */ jsx36("span", { className: "hidden sm:inline text-gray-400 dark:text-xtnd-light ml-2", children: "\u2192" })
    ] }, step.index)) })
  ] });
};
Workflow.displayName = "Workflow";
var Workflow_default = Workflow;
export {
  ActionsMenu,
  ActionsMenuDivider,
  ActionsMenuItem,
  Badge_default as Badge,
  Button_default as Button,
  Card_default as Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox_default as Checkbox,
  ClioIcon,
  ClioLinkButton_default as ClioLinkButton,
  DatePicker_default as DatePicker,
  Dialog_default as Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Drawer_default as Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  EmptyState_default as EmptyState,
  FileUpload_default as FileUpload,
  GoogleIcon,
  ICON_REGISTRY,
  Icon,
  Input_default as Input,
  Label_default as Label,
  LastUpdated_default as LastUpdated,
  LoadingSpinner_default as LoadingSpinner,
  MicrosoftIcon,
  Pagination_default as Pagination,
  PressHoldButton_default as PressHoldButton,
  Radio,
  RadioGroup,
  SecurityIcon,
  Select_default as Select,
  Skeleton,
  SkeletonCard,
  SkeletonForm,
  SkeletonTable,
  SlideOutPanel_default as SlideOutPanel,
  SlideOutPanelBody,
  SlideOutPanelFooter,
  SlideOutPanelHeader,
  Switch_default as Switch,
  Textarea_default as Textarea,
  Tooltip_default as Tooltip,
  UserBadge,
  Workflow_default as Workflow,
  XTNDLogo,
  XTNDLogoDark,
  XTNDLogoSquare,
  XTNDLogoSquareDark,
  formatDate,
  getMonthEnd,
  getMonthStart,
  getWeekEnd,
  getWeekStart,
  showToast,
  subtractDays,
  subtractWeeks,
  theme,
  toast
};
//# sourceMappingURL=index.mjs.map